import React, { useState, useEffect, useMemo, useRef } from 'react';
import trivanaLogoDefault from '../../Assets/Trivana_full_logo.png';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * NewDevelopmentTheme
 * ─────────────────────────────────────────────────────────────────────────────
 * 100% Pixel-Matched High-Fidelity Luxury UI matching the 4-Page Reference Mockup:
 *   Screen 1: Loading Page (Pastel wavy ribbon lines, concentric rings, shopping bag with golden heart, 0-100% progress)
 *   Screen 2: Home Page (Store profile avatar, 5 branded social circles, Explore CTA, 3 trust badges, tall layered organic waves with glowing orbs)
 *   Screen 3: Products Page (Header with cart badge, live search bar, luxury filter bottom-sheet modal, category chips, 2-col product grid with wishlist & +Add/stepper)
 *   Screen 4: Checkout Page (Cart items with notes & steppers, 3 delivery mode cards, address card with change, additional instructions, highlight total box, checkout CTA)
 * ─────────────────────────────────────────────────────────────────────────────
 */

// Rupee currency formatter
const formatPrice = (val) => {
  const num = Number(val || 0);
  return `₹${num.toLocaleString('en-IN')}`;
};

// Default Demo Items matching the screenshot when vendor catalog is empty
const DEMO_PRODUCTS = [
  {
    id: 'demo-p1',
    productId: 'demo-p1',
    name: 'Premium Perfume 100ml',
    productName: 'Premium Perfume 100ml',
    price: 1199,
    discountPrice: 899,
    mrp: 1199,
    imageUrl: 'https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=400&q=80',
    description: 'Long-lasting luxury fragrance crafted with pure organic extracts and amber base notes.',
    isBestseller: true,
    isVeg: true,
    modifierGroups: [
      {
        groupId: 'size-perfume',
        groupName: 'Select Size',
        isRequired: true,
        minSelection: 1,
        maxSelection: 1,
        options: [
          { optionId: 'sz-50', optionName: '50ml Pocket Edition', optionPrice: 0 },
          { optionId: 'sz-100', optionName: '100ml Luxury Glass Flacon', optionPrice: 200 }
        ]
      }
    ]
  },
  {
    id: 'demo-p2',
    productId: 'demo-p2',
    name: 'Casual Sneakers',
    productName: 'Casual Sneakers',
    price: 1699,
    discountPrice: 1299,
    mrp: 1699,
    imageUrl: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?auto=format&fit=crop&w=400&q=80',
    description: 'Lightweight, ultra-breathable daily wear sneakers for supreme all-day comfort.',
    isBestseller: true,
    isVeg: true,
    modifierGroups: [
      {
        groupId: 'size-shoes',
        groupName: 'Select Shoe Size',
        isRequired: true,
        minSelection: 1,
        maxSelection: 1,
        options: [
          { optionId: 'sz-uk7', optionName: 'UK 7 (US 8)', optionPrice: 0 },
          { optionId: 'sz-uk8', optionName: 'UK 8 (US 9)', optionPrice: 0 },
          { optionId: 'sz-uk9', optionName: 'UK 9 (US 10)', optionPrice: 100 }
        ]
      }
    ]
  },
  {
    id: 'demo-p3',
    productId: 'demo-p3',
    name: 'Wireless Headphones',
    productName: 'Wireless Headphones',
    price: 3499,
    discountPrice: 2499,
    mrp: 3499,
    imageUrl: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=400&q=80',
    description: 'Active noise cancelling studio over-ear wireless headphones with 40-hour battery life.',
    isBestseller: false,
    isVeg: true,
    modifierGroups: [
      {
        groupId: 'color-headphones',
        groupName: 'Color Edition',
        isRequired: false,
        minSelection: 0,
        maxSelection: 1,
        options: [
          { optionId: 'col-black', optionName: 'Midnight Black', optionPrice: 0 },
          { optionId: 'col-silver', optionName: 'Platinum Silver', optionPrice: 0 }
        ]
      }
    ]
  },
  {
    id: 'demo-p4',
    productId: 'demo-p4',
    name: 'Leather Handbag',
    productName: 'Leather Handbag',
    price: 2499,
    discountPrice: 1899,
    mrp: 2499,
    imageUrl: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&w=400&q=80',
    description: 'Handcrafted vegan leather handbag with golden metallic accents and roomy compartments.',
    isBestseller: false,
    isVeg: false
  },
  {
    id: 'demo-p5',
    productId: 'demo-p5',
    name: 'Sunglasses',
    productName: 'Sunglasses',
    price: 1399,
    discountPrice: 999,
    mrp: 1399,
    imageUrl: 'https://images.unsplash.com/photo-1511499767150-a48a237f0083?auto=format&fit=crop&w=400&q=80',
    description: 'UV400 polarized classic black sunglasses with lightweight durable frame.',
    isBestseller: true,
    isVeg: true
  },
  {
    id: 'demo-p6',
    productId: 'demo-p6',
    name: 'Smart Watch',
    productName: 'Smart Watch',
    price: 4999,
    discountPrice: 3499,
    mrp: 4999,
    imageUrl: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80',
    description: 'Next-gen AMOLED fitness tracker with heart rate, sleep tracking, and blood oxygen monitoring.',
    isBestseller: true,
    isVeg: true,
    modifierGroups: [
      {
        groupId: 'strap-watch',
        groupName: 'Strap Type',
        isRequired: true,
        minSelection: 1,
        maxSelection: 1,
        options: [
          { optionId: 'str-silicone', optionName: 'Sport Silicone Strap', optionPrice: 0 },
          { optionId: 'str-metal', optionName: 'Milanese Mesh Loop', optionPrice: 300 }
        ]
      }
    ]
  }
];

// Helper: Calculate robust pricing, MRP, and discount percentage for any product
const getProductPricingInfo = (product, getProductDisplayPriceMeta) => {
  if (!product) return { sellingPrice: 0, mrp: 0, hasDiscount: false, discountPercent: 0, discountAmount: 0, isEstimated: false };

  const raw = product.__raw || {};
  const meta = typeof getProductDisplayPriceMeta === 'function' ? getProductDisplayPriceMeta(product) : null;

  const rawItemPrice = Number(product.price ?? raw.price ?? 0);
  const rawBasePrice = Number(
    product.basePrice ??
    raw.basePrice ??
    raw.base_price ??
    product.costPrice ??
    raw.costPrice ??
    raw.cost_price ??
    0
  );

  const rawDiscountPrice = product.discountPrice != null && product.discountPrice !== ''
    ? Number(product.discountPrice)
    : (raw.discountPrice != null && raw.discountPrice !== ''
      ? Number(raw.discountPrice)
      : (raw.discount_price != null && raw.discount_price !== '' ? Number(raw.discount_price) : null));

  const rawMrp = Number(
    product.mrp ??
    raw.mrp ??
    product.originalPrice ??
    raw.originalPrice ??
    raw.original_price ??
    product.comparePrice ??
    raw.comparePrice ??
    raw.compare_price ??
    product.strikePrice ??
    raw.strikePrice ??
    raw.strike_price ??
    product.regularPrice ??
    raw.regularPrice ??
    raw.regular_price ??
    0
  );

  const rawDiscountPercentage = Number(
    product.discountPercentage ??
    raw.discount_percentage ??
    raw.discountPercentage ??
    product.discountPercent ??
    raw.discountPercent ??
    product.discount_percent ??
    raw.discount_percent ??
    0
  );

  let sellingPrice = rawItemPrice;
  let mrp = 0;

  // Case 1: Backend sends basePrice/costPrice higher than item price (e.g. basePrice: 20, price: 15)
  if (rawBasePrice > 0 && rawItemPrice > 0 && rawBasePrice > rawItemPrice) {
    sellingPrice = rawDiscountPrice && rawDiscountPrice < rawItemPrice ? rawDiscountPrice : rawItemPrice;
    mrp = rawMrp > rawBasePrice ? rawMrp : rawBasePrice;
  } else if (rawBasePrice > 0 && rawDiscountPrice && rawBasePrice > rawDiscountPrice) {
    sellingPrice = rawDiscountPrice;
    mrp = rawMrp > rawBasePrice ? rawMrp : rawBasePrice;
  }
  // Case 2: explicit MRP is greater than item price
  else if (rawMrp > 0 && rawItemPrice > 0 && rawMrp > rawItemPrice) {
    sellingPrice = rawDiscountPrice && rawDiscountPrice < rawItemPrice ? rawDiscountPrice : rawItemPrice;
    mrp = rawMrp;
  } else if (rawMrp > 0 && rawDiscountPrice && rawMrp > rawDiscountPrice) {
    sellingPrice = rawDiscountPrice;
    mrp = rawMrp;
  }
  // Case 3: rawItemPrice is greater than rawDiscountPrice -> rawItemPrice is MRP, rawDiscountPrice is selling price
  else if (rawDiscountPrice !== null && !isNaN(rawDiscountPrice) && rawDiscountPrice > 0 && rawItemPrice > rawDiscountPrice) {
    sellingPrice = rawDiscountPrice;
    mrp = rawMrp > rawItemPrice ? rawMrp : rawItemPrice;
  }
  // Case 4: rawDiscountPrice is greater than rawItemPrice -> rawDiscountPrice is MRP
  else if (rawDiscountPrice !== null && !isNaN(rawDiscountPrice) && rawDiscountPrice > 0 && rawDiscountPrice > rawItemPrice && rawItemPrice > 0) {
    sellingPrice = rawItemPrice;
    mrp = rawDiscountPrice;
  }
  // Case 5: discount percentage is provided directly without explicit discountPrice
  else if (rawDiscountPercentage > 0 && rawDiscountPercentage < 100 && (rawItemPrice > 0 || rawBasePrice > 0)) {
    const original = rawMrp > 0 ? rawMrp : (rawBasePrice > 0 ? rawBasePrice : rawItemPrice);
    mrp = original;
    sellingPrice = Math.round(original * (1 - rawDiscountPercentage / 100));
  }
  // Case 6: fallback when rawDiscountPrice exists and is positive
  else if (rawDiscountPrice !== null && !isNaN(rawDiscountPrice) && rawDiscountPrice > 0) {
    sellingPrice = rawDiscountPrice;
    mrp = rawMrp > rawDiscountPrice ? rawMrp : (rawBasePrice > rawDiscountPrice ? rawBasePrice : 0);
  } else {
    sellingPrice = rawItemPrice > 0 ? rawItemPrice : rawBasePrice;
    mrp = 0;
  }

  if (meta && meta.price > 0 && sellingPrice === 0) {
    sellingPrice = meta.price;
  }

  const hasDiscount = mrp > sellingPrice && sellingPrice > 0;
  const discountAmount = hasDiscount ? (mrp - sellingPrice) : 0;
  const calculatedPercent = hasDiscount && mrp > 0 ? Math.round(((mrp - sellingPrice) / mrp) * 100) : 0;
  const discountPercent = rawDiscountPercentage > 0 && rawDiscountPercentage < 100
    ? Math.round(rawDiscountPercentage)
    : calculatedPercent;

  return {
    sellingPrice,
    mrp,
    hasDiscount,
    discountPercent,
    discountAmount,
    isEstimated: Boolean(meta?.estimatedFromModifiers)
  };
};

// Helper: Determine if product has modifier groups, sizes, or addons
const hasCustomizations = (product) => {
  if (!product) return false;
  if (Array.isArray(product.modifierGroups) && product.modifierGroups.length > 0) return true;
  if (Array.isArray(product.addons) && product.addons.length > 0) return true;
  if (Array.isArray(product.customizations) && product.customizations.length > 0) return true;
  const raw = product.__raw || {};
  if (Array.isArray(raw.modifierGroups) && raw.modifierGroups.length > 0) return true;
  if (Array.isArray(raw.addons) && raw.addons.length > 0) return true;
  return false;
};

// Vector Icons
const Icons = {
  // Centerpiece: Shopping Bag with Golden Heart
  ShoppingBagHeart: ({ size = 64, bagColor = '#16192E', heartColor = '#D4A373' }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Handle */}
      <path
        d="M24 24V17C24 12.5817 27.5817 9 32 9C36.4183 9 40 12.5817 40 17V24"
        stroke={bagColor}
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      {/* Body */}
      <path
        d="M17 22H47L44.2 53C44.05 54.68 42.64 56 40.95 56H23.05C21.36 56 19.95 54.68 19.8 53L17 22Z"
        stroke={bagColor}
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      {/* Golden Heart */}
      <path
        d="M32 39.5C32 39.5 27.5 36.2 27.5 33C27.5 31.2 28.9 29.8 30.6 29.8C31.6 29.8 32.5 30.3 33 31C33.5 30.3 34.4 29.8 35.4 29.8C37.1 29.8 38.5 31.2 38.5 33C38.5 36.2 34 39.5 34 39.5L33 40.3L32 39.5Z"
        fill={heartColor}
      />
    </svg>
  ),

  // Top Hamburger Menu
  Hamburger: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16192E" strokeWidth="2.4" strokeLinecap="round">
      <line x1="4" y1="6.5" x2="20" y2="6.5" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17.5" x2="15" y2="17.5" />
    </svg>
  ),

  // Shopping Cart
  Cart: ({ color = '#16192E', size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="20" r="1.5" fill={color} />
      <circle cx="18" cy="20" r="1.5" fill={color} />
      <path d="M1 2h3.5l2.6 12.8a2 2 0 0 0 2 1.6h9.8a2 2 0 0 0 2-1.6L23 7H5.5" />
    </svg>
  ),

  // Search
  Search: ({ color = '#94A3B8', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7.5" />
      <line x1="21" y1="21" x2="16.5" y2="16.5" />
    </svg>
  ),

  // Filter
  Filter: ({ color = '#16192E', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1.5" y1="14" x2="6.5" y2="14" />
      <line x1="9.5" y1="8" x2="14.5" y2="8" />
      <line x1="17.5" y1="16" x2="22.5" y2="16" />
    </svg>
  ),

  // Chevron Left Back Arrow
  Back: ({ color = '#16192E', size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 19 8 12 15 5" />
    </svg>
  ),

  // Heart Wishlist
  Heart: ({ filled = false, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? '#EF4444' : 'none'} stroke={filled ? '#EF4444' : '#64748B'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),

  // Trash
  Trash: ({ size = 19, color = '#16192E' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  ),

  // Van / Truck Delivery
  Truck: ({ size = 24, color = '#16192E' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" rx="1" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),

  // Takeaway Bag
  TakeawayBag: ({ size = 24, color = '#16192E' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3L3 7v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-3-4z" />
      <line x1="3" y1="7" x2="21" y2="7" />
      <path d="M16 11a4 4 0 0 1-8 0" />
    </svg>
  ),

  // Drive Thru Car
  DriveThruCar: ({ size = 24, color = '#16192E' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14m-12 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm12 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
      <path d="M3 17V9a2 2 0 0 1 2-2h2l2.5-4h5l2.5 4H19a2 2 0 0 1 2 2v8" />
    </svg>
  ),

  // Shield Check
  ShieldCheck: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16192E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  ),

  // Headset
  Headset: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16192E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  ),

  // Location Pin
  LocationPin: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16192E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),

  // Note
  Note: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),

  // Close
  Close: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),

  // Chevron Right
  ChevronRight: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),

  // Sparkles Icon
  Sparkles: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16192E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 4.886a2 2 0 001.202 1.202L20 11l-4.886 1.912a2 2 0 00-1.202 1.202L12 19l-1.912-4.886a2 2 0 00-1.202-1.202L4 11l4.886-1.912a2 2 0 001.202-1.202L12 3z" />
    </svg>
  ),

  // Arrow Up (Price Low to High)
  ArrowUp: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  ),

  // Arrow Down (Price High to Low)
  ArrowDown: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  )
};

const NewDevelopmentTheme = ({
  restaurant,
  isShopMode,
  restaurantOpen = true,
  closedMessage = '',
  categories = [],
  products = [],
  groupedMenuSections = [],
  cart = [],
  fulfillmentType: parentFulfillmentType,
  selectFulfillmentType,
  addToCart,
  updateQuantity,
  removeFromCart,
  getCartCount,
  getCartTotal,
  getCartPricing,
  getProductDisplayPriceMeta,
  getProductQuantity,
  openCheckoutModal,
  openCartItemAddonModal,
  setDescriptionPopupProduct,
  setShowClearCartConfirm,
  clearCart,
  logoLoadError,
  setLogoLoadError,
  isMobile,
  currentTheme,
  // Customization & Addon props from TemplatedMenuPage
  showAddonModal,
  setShowAddonModal,
  currentProduct,
  setCurrentProduct,
  availableAddons = [],
  selectionRulesByGroup = {},
  selectedAddons = [],
  setSelectedAddons,
  toggleAddonSelection,
  closeAddonModal,
  loadingAddons = false,
  addToCartWithAddons,
  openAddonModalWithProduct,
  showAddonSelectionModal,
  fetchAddons,
  applyProductCustomizations,
  validateGroupSelections,
  pendingSpecialInstructions,
  setPendingSpecialInstructions,
  editingCartItemId,
  addCartItem,
  setCart,
  deliverySettings,
  deliverySlabInsight,
  // Checkout & Coupon props
  checkoutForm,
  setCheckoutForm,
  handleApplyCoupon,
  handleRemoveCoupon,
  couponCode = '',
  setCouponCode,
  appliedCoupon,
  availableCoupons = [],
  couponsLoading = false,
  couponsError = '',
  handleToggleCoupons,
  formatCouponDescription: propFormatCouponDescription,
  formatCouponExpiry: propFormatCouponExpiry,
  fetchAvailableCoupons,
  onlinePaymentConfigured = true,
  trivanaLogo: propTrivanaLogo,
  resolveMediaUrl: propResolveMediaUrl
}) => {
  // Safe Image URL Resolver for platform gallery and uploaded media
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (typeof propResolveMediaUrl === 'function') {
      try {
        const resolved = propResolveMediaUrl(url);
        if (resolved) return resolved;
      } catch {
        // fallback to internal logic
      }
    }
    const raw = String(url).trim();
    if (!raw) return '';
    if (/^(https?:|data:|blob:)/i.test(raw)) {
      return raw;
    }
    const normalized = raw.startsWith('/') ? raw : `/${raw}`;
    const apiOrigin = (() => {
      try {
        const hostname = window.location.hostname || 'localhost';
        return `${window.location.protocol}//${hostname}:8081`;
      } catch {
        return 'http://localhost:8081';
      }
    })();
    return `${apiOrigin}${normalized}`;
  };

  // Navigation State: 'loading' | 'home' | 'products' | 'checkout'
  const [activeScreen, setActiveScreen] = useState('loading');
  const [previousScreen, setPreviousScreen] = useState('home');
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Search & Filter State for Screen 3
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [vegFilter, setVegFilter] = useState('ALL'); // 'ALL' | 'VEG' | 'NON_VEG'
  const [sortBy, setSortBy] = useState('DEFAULT'); // 'DEFAULT' | 'PRICE_LOW' | 'PRICE_HIGH'
  const [inStockOnly, setInStockOnly] = useState(false);

  // Wishlist persisted in localStorage
  const wishlistStorageKey = `trivana_wishlist_${restaurant?.restaurantId || restaurant?.id || 'default'}`;
  const [wishlist, setWishlist] = useState(() => {
    try {
      const saved = localStorage.getItem(wishlistStorageKey);
      if (saved) return new Set(JSON.parse(saved));
    } catch (e) {}
    return new Set();
  });

  const toggleWishlist = (productId, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setWishlist((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      try {
        localStorage.setItem(wishlistStorageKey, JSON.stringify(Array.from(next)));
      } catch (err) {}
      return next;
    });
  };

  // Full-Size Product Detail Modal State
  const [selectedDetailProduct, setSelectedDetailProduct] = useState(null);
  const [detailAvailableAddons, setDetailAvailableAddons] = useState([]);
  const [detailRulesByGroup, setDetailRulesByGroup] = useState({});
  const [detailSelectedAddons, setDetailSelectedAddons] = useState([]);
  const [detailSpecialInstructions, setDetailSpecialInstructions] = useState('');
  const [detailQuantity, setDetailQuantity] = useState(1);
  const [detailLoadingAddons, setDetailLoadingAddons] = useState(false);

  // Checkout State for Screen 4
  const [deliveryOption, setDeliveryOption] = useState(() => {
    if (parentFulfillmentType === 'DRIVETHROUGH') return 'DRIVE_THRU';
    if (parentFulfillmentType === 'TAKEAWAY') return 'TAKEAWAY';
    return 'DELIVERY';
  });

  useEffect(() => {
    if (parentFulfillmentType === 'DRIVETHROUGH') setDeliveryOption('DRIVE_THRU');
    else if (parentFulfillmentType === 'TAKEAWAY') setDeliveryOption('TAKEAWAY');
    else if (parentFulfillmentType === 'DELIVERY') setDeliveryOption('DELIVERY');
  }, [parentFulfillmentType]);

  const [itemNotes, setItemNotes] = useState({});
  const [editingNoteItemId, setEditingNoteItemId] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [showCouponList, setShowCouponList] = useState(false);

  const formatCouponDesc = (coupon) => {
    if (propFormatCouponDescription) return propFormatCouponDescription(coupon);
    const discountType = String(coupon?.discountType || '').toUpperCase();
    const discountValue = Number(coupon?.discountValue || 0);
    const minOrderValue = Number(coupon?.minimumOrderValue || 0);
    const upToValue = Number(coupon?.upToValue ?? coupon?.up_to_value ?? 0);

    let text = discountType === 'PERCENT' ? `${discountValue}% Off` : `₹${discountValue.toFixed(0)} Off`;
    if (minOrderValue > 0) text += ` on orders above ₹${minOrderValue.toFixed(0)}`;
    if (discountType === 'PERCENT' && upToValue > 0) text += ` • Up to ₹${upToValue.toFixed(0)}`;
    return text;
  };

  const formatCouponExp = (coupon) => {
    if (propFormatCouponExpiry) return propFormatCouponExpiry(coupon);
    const validTill = coupon?.validTill ? new Date(coupon.validTill) : null;
    if (!validTill || Number.isNaN(validTill.getTime())) return '';
    return `Valid till ${validTill.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  };

  // Luxury Brand Emblem for Loading screen & fallback logos (replaces cartoon bag)
  const renderStoreEmblem = (size = 120, fontSize = 52) => {
    if (restaurant?.logoUrl && !logoLoadError) {
      return (
        <img
          src={resolveImageUrl(restaurant.logoUrl)}
          alt={storeName}
          onError={() => setLogoLoadError && setLogoLoadError(true)}
          style={{
            width: `${Math.round(size * 0.72)}px`,
            height: `${Math.round(size * 0.72)}px`,
            borderRadius: '50%',
            objectFit: 'cover'
          }}
        />
      );
    }

    const initial = (storeName || 'T').trim().charAt(0).toUpperCase();

    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #16192E 0%, #1E2340 50%, #2A3158 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          boxShadow: '0 8px 24px rgba(22, 25, 46, 0.3)',
          overflow: 'hidden'
        }}
      >
        {/* Subtle decorative inner gold rim */}
        <div
          style={{
            position: 'absolute',
            inset: '3px',
            borderRadius: '50%',
            border: '1.5px solid rgba(212, 163, 115, 0.45)',
            pointerEvents: 'none'
          }}
        />

        {/* Floating tiny spark */}
        <div
          style={{
            position: 'absolute',
            top: `${Math.round(size * 0.12)}px`,
            right: `${Math.round(size * 0.18)}px`,
            fontSize: `${Math.max(10, Math.round(size * 0.14))}px`,
            color: '#E5B887',
            opacity: 0.85
          }}
        >
          ✦
        </div>

        {/* Monogram Letter in Playfair Display Serif */}
        <span
          className="ndt-serif"
          style={{
            fontSize: `${fontSize}px`,
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            background: 'linear-gradient(180deg, #FFFFFF 25%, #E5B887 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.35))'
          }}
        >
          {initial}
        </span>
      </div>
    );
  };
  const [orderInstructions, setOrderInstructions] = useState(checkoutForm?.deliveryInstructions || '');
  const [customAddress, setCustomAddress] = useState(checkoutForm?.addressLine || restaurant?.address || restaurant?.city || 'Jaipur, Rajasthan, India');
  const [editingAddress, setEditingAddress] = useState(false);

  useEffect(() => {
    if (checkoutForm?.addressLine) {
      setCustomAddress(checkoutForm.addressLine);
    }
  }, [checkoutForm?.addressLine]);

  // Category horizontal scroll ref for desktop
  const categoryScrollRef = useRef(null);
  const scrollCategories = (offset) => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({ left: offset, behavior: 'smooth' });
    }
  };

  // Hamburger drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Smooth, Fast Luxury Loading Animation (0% to 100% in ~450ms)
  const loadingIntervalRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  const skipLoading = () => {
    if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    setLoadingProgress(100);
    setActiveScreen('home');
  };

  useEffect(() => {
    let current = 0;
    loadingIntervalRef.current = setInterval(() => {
      current += 5;
      if (current >= 100) {
        setLoadingProgress(100);
        if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
        loadingTimeoutRef.current = setTimeout(() => {
          setActiveScreen('home');
        }, 150);
      } else {
        setLoadingProgress(current);
      }
    }, 20);
    return () => {
      if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  // Browser Back Button Navigation (popstate)
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state && e.state.ndtScreen) {
        setActiveScreen(e.state.ndtScreen);
      } else {
        setActiveScreen('home');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Screen transition with history
  const navigateTo = (screen) => {
    if (screen !== activeScreen) {
      setPreviousScreen(activeScreen);
      setActiveScreen(screen);
      try {
        window.history.pushState({ ndtScreen: screen }, '', window.location.pathname);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        // Fallback
      }
    }
  };

  // Sync delivery mode with parent
  const handleSelectDeliveryOption = (opt) => {
    setDeliveryOption(opt);
    if (selectFulfillmentType) {
      selectFulfillmentType(opt === 'DRIVE_THRU' ? 'DRIVETHROUGH' : opt);
    }
  };

  // Helpers for item validation
  const isItemOutOfStock = (p) => {
    if (!p) return false;
    return p.inStock === false || p.available === false || p.isAvailable === false;
  };

  const isItemVeg = (p) => {
    if (!p) return true;
    return !(p.isVeg === false || p.isVeg === 'false' || p.itemType === 'NON_VEG');
  };

  const normalizeText = (val) => String(val || '').trim().toLowerCase();

  const getProductCategoryId = (p) => {
    return (
      p?.productCategory?.categoryId ??
      p?.productCategory?.productCategoryId ??
      p?.productCategory?.id ??
      p?.productCategoryId ??
      p?.product_category_id ??
      p?.categoryId ??
      p?.category_id ??
      null
    );
  };

  const getProductCategoryName = (p) => {
    return (
      p?.productCategory?.categoryName ??
      p?.productCategory?.name ??
      (typeof p?.productCategory === 'string' ? p.productCategory : null) ??
      p?.categoryName ??
      p?.category ??
      ''
    );
  };

  // Normalized Categories list for chips and filtering (100% resilient across restaurant and shop mode)
  const catalogCategories = useMemo(() => {
    const list = [];
    const seen = new Set();

    // 1. If groupedMenuSections is provided and has sections with items, prioritize it (matches all other themes)
    if (Array.isArray(groupedMenuSections) && groupedMenuSections.length > 0) {
      groupedMenuSections.forEach((section, idx) => {
        if (!section || !section.name) return;
        const rawId = section.id != null && section.id !== '' ? String(section.id) : null;
        const name = String(section.name || '').trim();
        const uniqueKey = rawId != null ? `id-${rawId}` : `name-${normalizeText(name)}`;
        if (!seen.has(uniqueKey) && name) {
          seen.add(uniqueKey);
          list.push({
            id: rawId != null ? String(rawId) : uniqueKey,
            rawId: rawId,
            label: name,
            name: name
          });
        }
      });
      if (list.length > 0) return list;
    }

    const validProducts = Array.isArray(products) ? products : [];
    const productCatIds = new Set();
    const productCatNames = new Set();
    validProducts.forEach((p) => {
      const cid = getProductCategoryId(p);
      const cname = normalizeText(getProductCategoryName(p));
      if (cid != null && cid !== '') productCatIds.add(String(cid));
      if (cname) productCatNames.add(cname);
    });

    // 2. Process categories prop (from backend)
    (Array.isArray(categories) ? categories : []).forEach((c, idx) => {
      if (!c) return;
      const rawId =
        c?.productCategoryId ??
        c?.product_category_id ??
        c?.categoryId ??
        c?.category_id ??
        c?.id ??
        c?._id;
      const name = String(c?.categoryName ?? c?.name ?? c?.category_name ?? '').trim();
      const normName = normalizeText(name);
      const strRawId = rawId != null && rawId !== '' ? String(rawId) : null;

      // If we have products, only include category if products exist for it
      if (validProducts.length > 0) {
        const hasProducts =
          (strRawId != null && productCatIds.has(strRawId)) ||
          (normName && productCatNames.has(normName));
        if (!hasProducts) return;
      }

      const uniqueKey = strRawId != null ? `id-${strRawId}` : `name-${normName}`;
      if (!seen.has(uniqueKey) && (name || strRawId != null)) {
        seen.add(uniqueKey);
        list.push({
          id: strRawId != null ? String(strRawId) : uniqueKey,
          rawId: strRawId,
          label: name || `Category ${idx + 1}`,
          name: name
        });
      }
    });

    // 3. Extract any categories present in products that weren't in categories
    if (validProducts.length > 0) {
      validProducts.forEach((p, idx) => {
        const name = String(getProductCategoryName(p) || '').trim();
        const catId = getProductCategoryId(p);
        const strCatId = catId != null && catId !== '' ? String(catId) : null;
        const normName = normalizeText(name);

        const alreadyCovered = list.some(
          (c) =>
            (strCatId != null && c.rawId != null && String(c.rawId) === strCatId) ||
            (normName && normalizeText(c.name) === normName)
        );

        const uniqueKey = strCatId != null ? `id-${strCatId}` : `name-${normName}`;
        if (!alreadyCovered && !seen.has(uniqueKey) && (name || strCatId != null)) {
          seen.add(uniqueKey);
          list.push({
            id: strCatId != null ? String(strCatId) : uniqueKey,
            rawId: strCatId,
            label: name || `Category ${idx + 1}`,
            name: name
          });
        }
      });
    }

    return list;
  }, [categories, products, groupedMenuSections]);

  // Catalog products: use live backend products ONLY (no demo items leaking into real stores)
  const catalogProducts = useMemo(() => {
    if (Array.isArray(products) && products.length > 0) return products;
    return [];
  }, [products]);

  // Live Cart Items: strictly real cart items (no fake ghost items)
  const displayCart = useMemo(() => {
    return Array.isArray(cart) ? cart : [];
  }, [cart]);

  // Live cart count & calculations
  const totalCartCount = (cart && cart.length > 0)
    ? (getCartCount ? getCartCount() : cart.reduce((acc, i) => acc + (i.quantity || 1), 0))
    : 0;

  const pricing = useMemo(() => {
    if (cart && cart.length > 0 && typeof getCartPricing === 'function') {
      try {
        const p = getCartPricing();
        if (p && typeof p.total === 'number') {
          return {
            subtotal: p.subtotal || 0,
            deliveryCharge: p.deliveryCharge || 0,
            taxCharges: p.taxAmount || 0,
            discountAmount: p.discountAmount || 0,
            grandTotal: p.total || 0,
            belowMinimum: Boolean(p.belowMinimum),
            minOrderAmount: Number(p.minOrderAmount || deliverySettings?.minOrderAmount || 0),
            deficit: Number(p.deficit || 0)
          };
        }
      } catch (e) {
        // Fall through
      }
    }
    if (!cart || cart.length === 0) {
      return {
        subtotal: 0,
        deliveryCharge: 0,
        taxCharges: 0,
        discountAmount: 0,
        grandTotal: 0,
        belowMinimum: false,
        minOrderAmount: 0,
        deficit: 0
      };
    }
    const sub = displayCart.reduce((acc, i) => acc + (Number(i.price || 0) * Number(i.quantity || 1)), 0);
    const del = deliveryOption === 'DELIVERY' ? Number(deliverySettings?.deliveryCharge || 40) : 0;
    const tax = 0;
    return {
      subtotal: sub,
      deliveryCharge: del,
      taxCharges: tax,
      discountAmount: 0,
      grandTotal: sub + del + tax,
      belowMinimum: false,
      minOrderAmount: 0,
      deficit: 0
    };
  }, [getCartPricing, cart, displayCart, deliveryOption, deliverySettings]);

  // Delivery Slab Incentive Message
  const deliverySlabMessage = useMemo(() => {
    if (deliveryOption !== 'DELIVERY' && parentFulfillmentType !== 'DELIVERY') {
      return null;
    }

    // 1. Check parent-provided deliverySlabInsight first
    if (deliverySlabInsight?.shortMessage) {
      return deliverySlabInsight.shortMessage;
    }

    // 2. Or compute dynamically from deliverySettings or restaurant delivery slabs
    const slabs = Array.isArray(deliverySettings?.slabs)
      ? deliverySettings.slabs
      : (Array.isArray(restaurant?.deliverySlabs) ? restaurant.deliverySlabs : []);

    const isSlabEnabled = deliverySettings?.isSlabPricingEnabled !== false;
    if (!isSlabEnabled || slabs.length === 0) {
      return null;
    }

    const subtotal = Number(pricing.subtotal || 0);
    if (subtotal <= 0) return null;

    const currentDeliveryCharge = Number(pricing.deliveryCharge || 0);

    const normalizedSlabs = slabs
      .filter((s) => s && s.isActive !== false)
      .map((s) => ({
        min: Number(s.minOrderAmount || s.min || 0),
        charge: Number(s.deliveryCharge || s.charge || 0)
      }))
      .sort((a, b) => a.min - b.min);

    const nextSlab = normalizedSlabs.find((s) => s.min > subtotal && s.charge < currentDeliveryCharge);
    if (!nextSlab) {
      return null;
    }

    const delta = Math.max(nextSlab.min - subtotal, 0);
    if (delta <= 0) {
      return null;
    }

    if (nextSlab.charge <= 0) {
      return `Add ₹${delta.toFixed(0)} more for FREE Delivery`;
    }

    return `Add ₹${delta.toFixed(0)} more to reduce delivery charges to ₹${nextSlab.charge.toFixed(0)}`;
  }, [deliveryOption, parentFulfillmentType, deliverySlabInsight, deliverySettings, restaurant, pricing.subtotal, pricing.deliveryCharge]);

  // Quantity helper
  const getItemQuantity = (productId) => {
    if (getProductQuantity && cart && cart.length > 0) {
      return getProductQuantity(productId);
    }
    const item = (cart && cart.length > 0 ? cart : displayCart).find((i) => (i.productId || i.id) === productId);
    return item ? (item.quantity || 0) : 0;
  };

  // Load modifier groups / addons when a product is opened in the Full-Size Detail Modal
  useEffect(() => {
    if (!selectedDetailProduct) {
      setDetailSelectedAddons([]);
      setDetailSpecialInstructions('');
      setDetailQuantity(1);
      setDetailAvailableAddons([]);
      setDetailRulesByGroup({});
      return;
    }

    setDetailQuantity(1);
    setDetailSpecialInstructions('');

    let isCancelled = false;
    const loadCustomizations = async () => {
      setDetailLoadingAddons(true);
      try {
        const raw = selectedDetailProduct.__raw || {};
        const embeddedGroups = Array.isArray(selectedDetailProduct.modifierGroups)
          ? selectedDetailProduct.modifierGroups
          : (Array.isArray(raw.modifierGroups) ? raw.modifierGroups : []);
        const embeddedAddons = Array.isArray(selectedDetailProduct.addons)
          ? selectedDetailProduct.addons
          : (Array.isArray(raw.addons) ? raw.addons : []);

        if (embeddedGroups.length > 0 || embeddedAddons.length > 0) {
          const flattened = [];
          const rules = {};

          embeddedGroups.forEach((group) => {
            const groupId = String(group?.groupId || `group-${group?.groupName || 'addons'}`);
            rules[groupId] = {
              groupName: group?.groupName || 'Add-ons',
              isRequired: !!group?.isRequired,
              minSelection: Number(group?.minSelection || 0),
              maxSelection: Number(group?.maxSelection || 99),
              displayOrder: Number(group?.displayOrder ?? 9999),
              dependsOnGroupId: group?.dependsOnGroupId != null ? String(group.dependsOnGroupId) : null,
              dependsOnGroupName: group?.dependsOnGroupName || null,
              dynamicLabel: !!group?.dynamicLabel
            };

            (Array.isArray(group?.options) ? group.options : []).forEach((opt) => {
              flattened.push({
                addonId: opt?.legacyAddonId || opt?.optionId,
                addonName: opt?.optionName,
                addonPrice: Number(opt?.optionPrice || 0),
                basePrice: Number(opt?.optionPrice || 0),
                optionId: opt?.optionId || null,
                legacyAddonId: opt?.legacyAddonId || null,
                contextKey: opt?.contextKey || null,
                groupId,
                groupName: group?.groupName || 'Add-ons',
                groupDisplayOrder: Number(group?.displayOrder ?? 9999)
              });
            });
          });

          embeddedAddons.forEach((addon) => {
            flattened.push({
              addonId: addon?.addonId ?? addon?.id ?? null,
              addonName: addon?.addonName ?? addon?.name ?? '',
              addonPrice: Number(addon?.addonPrice ?? addon?.price ?? 0),
              basePrice: Number(addon?.addonPrice ?? addon?.price ?? 0),
              optionId: null,
              legacyAddonId: addon?.legacyAddonId ?? addon?.addonId ?? addon?.id ?? null,
              groupId: 'legacy-default',
              groupName: 'Add-ons',
              groupDisplayOrder: Number(addon?.displayOrder ?? 9999)
            });
          });

          if (!isCancelled) {
            setDetailAvailableAddons(flattened);
            setDetailRulesByGroup(rules);

            // Auto-select first option for required groups with minSelection >= 1
            const autoSelections = [];
            Object.entries(rules).forEach(([gId, r]) => {
              if (Number(r.minSelection || 0) >= 1) {
                const opts = flattened.filter((o) => o.groupId === gId);
                if (opts.length > 0) {
                  autoSelections.push(opts[0]);
                }
              }
            });
            setDetailSelectedAddons(autoSelections);
          }
          return;
        }

        // Try fetchAddons prop if available
        if (typeof fetchAddons === 'function') {
          const fetched = await fetchAddons(selectedDetailProduct);
          if (!isCancelled && Array.isArray(fetched) && fetched.length > 0) {
            setDetailAvailableAddons(fetched);
            setDetailRulesByGroup(selectionRulesByGroup || {});

            const autoSelections = [];
            Object.entries(selectionRulesByGroup || {}).forEach(([gId, r]) => {
              if (Number(r.minSelection || 0) >= 1) {
                const opts = fetched.filter((o) => o.groupId === gId);
                if (opts.length > 0) {
                  autoSelections.push(opts[0]);
                }
              }
            });
            setDetailSelectedAddons(autoSelections);
            return;
          }
        }

        if (!isCancelled) {
          setDetailAvailableAddons([]);
          setDetailRulesByGroup({});
        }
      } catch (err) {
        console.warn('Failed to load item customizations:', err);
      } finally {
        if (!isCancelled) setDetailLoadingAddons(false);
      }
    };

    loadCustomizations();

    return () => {
      isCancelled = true;
    };
  }, [selectedDetailProduct, fetchAddons, selectionRulesByGroup]);

  // Toggle addon in detail modal
  const toggleDetailAddon = (addon) => {
    const currentKey = String(addon?.optionId || addon?.legacyAddonId || addon?.addonId || addon?.addonName || '');
    setDetailSelectedAddons((prev) => {
      const exists = prev.find((a) => String(a?.optionId || a?.legacyAddonId || a?.addonId || a?.addonName || '') === currentKey);
      if (exists) {
        return prev.filter((a) => String(a?.optionId || a?.legacyAddonId || a?.addonId || a?.addonName || '') !== currentKey);
      }
      const groupId = addon?.groupId;
      const rule = detailRulesByGroup?.[String(groupId)];
      if (rule) {
        const selectedInGroup = prev.filter((item) => item?.groupId === groupId);
        if (Number(rule.maxSelection || 99) === 1 && selectedInGroup.length >= 1) {
          const withoutGroup = prev.filter((item) => item?.groupId !== groupId);
          return [...withoutGroup, addon];
        }
        if (selectedInGroup.length >= Number(rule.maxSelection || 99)) {
          alert(`Maximum ${rule.maxSelection} selection allowed in ${rule.groupName}`);
          return prev;
        }
      }
      return [...prev, addon];
    });
  };

  // Validate selections in detail modal
  const validateDetailSelections = () => {
    const grouped = (detailSelectedAddons || []).reduce((acc, item) => {
      const key = item?.groupId || 'default';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    for (const [groupId, rule] of Object.entries(detailRulesByGroup || {})) {
      const selectedCount = grouped[groupId] || 0;
      const min = Number(rule?.minSelection || 0);
      const max = Number(rule?.maxSelection || 99);

      if (rule?.isRequired && selectedCount === 0) {
        alert(`Please select an option for ${rule.groupName}`);
        return false;
      }
      if (selectedCount < min) {
        alert(`Select minimum ${min} option(s) for ${rule.groupName}`);
        return false;
      }
      if (selectedCount > max) {
        alert(`Select maximum ${max} option(s) for ${rule.groupName}`);
        return false;
      }
    }
    return true;
  };

  // Confirm and add to cart from detail modal
  const handleConfirmDetailAddToCart = () => {
    if (!selectedDetailProduct) return;
    if (!validateDetailSelections()) return;

    const product = selectedDetailProduct;
    const addons = detailSelectedAddons;
    const instructions = detailSpecialInstructions;
    const qty = detailQuantity > 0 ? detailQuantity : 1;

    if (typeof addCartItem === 'function') {
      for (let i = 0; i < qty; i++) {
        addCartItem(product, addons, instructions);
      }
    } else if (typeof addToCartWithAddons === 'function') {
      addToCartWithAddons(product, addons);
    } else if (typeof addToCart === 'function') {
      addToCart(product, qty);
    }

    // Close detail modal immediately
    setSelectedDetailProduct(null);
    setDetailSelectedAddons([]);
    setDetailSpecialInstructions('');
    setDetailQuantity(1);
  };

  // Add to cart / addons trigger from card
  const handleAddToCart = (product) => {
    const isCustomizable = hasCustomizations(product);
    if (isCustomizable) {
      if (typeof openAddonModalWithProduct === 'function') {
        openAddonModalWithProduct(product, [], '', null);
      } else if (typeof showAddonSelectionModal === 'function') {
        showAddonSelectionModal(product);
      } else if (typeof addToCart === 'function') {
        addToCart(product, 1);
      } else {
        setSelectedDetailProduct(product);
      }
      return;
    }

    if (typeof addToCart === 'function') {
      addToCart(product, 1);
    }
  };

  // Stepper change for catalog item
  const handleProductQuantityChange = (product, delta) => {
    const isCustomizable = hasCustomizations(product);
    if (isCustomizable && delta > 0) {
      // Re-prompt customization on + for customizable products
      handleAddToCart(product);
      return;
    }

    const pId = product.id || product.productId;
    const cartItem = (cart || []).find((i) => (i.productId || i.id) === pId);
    if (cartItem) {
      if (updateQuantity) {
        updateQuantity(cartItem.cartItemId, delta);
      }
    } else if (delta > 0) {
      handleAddToCart(product);
    }
  };

  // Stepper change for cart item
  const handleCartItemQuantityChange = (cartItem, delta) => {
    if (updateQuantity) {
      updateQuantity(cartItem.cartItemId, delta);
    }
  };

  // Remove single item
  const handleRemoveCartItem = (cartItem) => {
    if (removeFromCart) {
      removeFromCart(cartItem.cartItemId);
    }
  };

  // Clear all items without double confirm prompt
  const handleClearCart = () => {
    if (setShowClearCartConfirm) {
      setShowClearCartConfirm(true);
    } else if (clearCart) {
      clearCart();
    } else if (removeFromCart && cart) {
      cart.forEach((i) => removeFromCart(i.cartItemId));
    }
  };

  // Save per-item note and persist directly to cart object
  const handleSaveItemNote = (cartItemId, noteText) => {
    if (typeof setCart === 'function') {
      setCart((prev) =>
        prev.map((it) =>
          (it.cartItemId === cartItemId || it.id === cartItemId || it.productId === cartItemId)
            ? { ...it, specialInstructions: noteText }
            : it
        )
      );
    }
    setItemNotes((prev) => ({ ...prev, [cartItemId]: noteText }));
    setEditingNoteItemId(null);
  };

  // Count active filters for badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (vegFilter !== 'ALL') count++;
    if (sortBy !== 'DEFAULT') count++;
    if (inStockOnly) count++;
    return count;
  }, [vegFilter, sortBy, inStockOnly]);

  // Filtered & Sorted Products with reliable category & veg detection
  const filteredProducts = useMemo(() => {
    let list = [...catalogProducts];

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) => {
        const name = (p.productName || p.name || '').toLowerCase();
        const desc = (p.description || '').toLowerCase();
        return name.includes(q) || desc.includes(q);
      });
    }

    // Category filter (No Bestseller, No New; robust ID and name matching)
    if (selectedCategory !== 'ALL') {
      if (selectedCategory === 'WISHLIST') {
        list = list.filter((p) => wishlist.has(p.id || p.productId));
      } else if (selectedCategory === 'OFFERS') {
        list = list.filter((p) => p.discountPrice || (p.mrp && p.mrp > p.price));
      } else {
        const activeCategory = catalogCategories.find(
          (c) => String(c.id) === String(selectedCategory) || (c.rawId && String(c.rawId) === String(selectedCategory))
        );
        list = list.filter((p) => {
          const pCatId = getProductCategoryId(p);
          const pCatName = normalizeText(getProductCategoryName(p));
          const selStr = String(selectedCategory);

          // 1. Direct ID match
          if (
            pCatId != null &&
            (String(pCatId) === selStr ||
              (activeCategory?.rawId != null && String(pCatId) === String(activeCategory.rawId)) ||
              (activeCategory?.id != null && String(pCatId) === String(activeCategory.id)))
          ) {
            return true;
          }
          // 2. Name match (case-insensitive & trimmed)
          if (activeCategory?.name && pCatName && normalizeText(activeCategory.name) === pCatName) {
            return true;
          }
          if (activeCategory?.label && pCatName && normalizeText(activeCategory.label) === pCatName) {
            return true;
          }
          if (pCatName && (pCatName === normalizeText(selStr) || selStr === `name-${pCatName}`)) {
            return true;
          }
          return false;
        });
      }
    }

    // Veg / Non-Veg using consistent helper
    if (vegFilter === 'VEG') {
      list = list.filter((p) => isItemVeg(p));
    } else if (vegFilter === 'NON_VEG') {
      list = list.filter((p) => !isItemVeg(p));
    }

    // In-Stock only
    if (inStockOnly) {
      list = list.filter((p) => !isItemOutOfStock(p));
    }

    // Sorting
    if (sortBy === 'PRICE_LOW') {
      list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortBy === 'PRICE_HIGH') {
      list.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    }

    return list;
  }, [catalogProducts, searchQuery, selectedCategory, vegFilter, inStockOnly, sortBy, wishlist, catalogCategories]);

  // Reset selected category to ALL if current selection no longer exists in catalogCategories
  useEffect(() => {
    if (selectedCategory !== 'ALL' && selectedCategory !== 'WISHLIST' && selectedCategory !== 'OFFERS') {
      const exists = catalogCategories.some(
        (c) => String(c.id) === String(selectedCategory) || (c.rawId && String(c.rawId) === String(selectedCategory))
      );
      if (!exists && catalogCategories.length > 0) {
        setSelectedCategory('ALL');
      }
    }
  }, [catalogCategories, selectedCategory]);

  // Robust WhatsApp number resolution with Country Code
  const getWhatsAppNumber = () => {
    const rawNumber =
      restaurant?.whatsappNumber ||
      restaurant?.whatsapp_number ||
      restaurant?.contactPhone ||
      restaurant?.contact_phone ||
      restaurant?.phone ||
      '';

    if (!rawNumber) return '';

    let digits = String(rawNumber).replace(/\D/g, '');
    if (!digits) return '';

    // Strip leading trunk zero (e.g. 09876543210 -> 9876543210)
    if (digits.startsWith('0')) {
      digits = digits.replace(/^0+/, '');
    }

    // Determine country code from database or fallback to 91 (India)
    const rawCC =
      restaurant?.whatsappCountryCode ||
      restaurant?.whatsapp_country_code ||
      restaurant?.countryCode ||
      restaurant?.country_code ||
      restaurant?.country_phone_code ||
      '91';
    const cc = String(rawCC).replace(/\D/g, '') || '91';

    // If standard 10-digit mobile number, prepend country code
    if (digits.length === 10) {
      digits = cc + digits;
    }

    return digits;
  };

  const formattedWhatsAppNumber = getWhatsAppNumber();
  const phone = restaurant?.contactPhone || restaurant?.contact_phone || '';
  const instagram = restaurant?.instagramUrl || restaurant?.instagram_url || '';
  const facebook = restaurant?.facebookUrl || restaurant?.facebook_url || '';
  const mapUrl = restaurant?.mapLocation || restaurant?.map_location || '';

  const storeName = restaurant?.restaurantName || restaurant?.shopName || restaurant?.name || (isShopMode ? 'Your Daily Essentials' : 'Your One Stop Shop');
  const storeTagline = restaurant?.cuisineType || 'Quality Products for a Better Everyday';

  // Filtered list of valid social links (no dead clicks, valid phone & country code)
  const socialLinks = useMemo(() => {
    const list = [];
    if (formattedWhatsAppNumber) {
      list.push({
        id: 'wa',
        label: 'WhatsApp',
        url: `https://wa.me/${formattedWhatsAppNumber}`,
        bg: '#25D366',
        color: '#FFFFFF',
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2ZM12.05 20.15C10.57 20.15 9.12 19.76 7.85 19.01L7.55 18.83L4.44 19.65L5.27 16.61L5.07 16.29C4.24 14.98 3.81 13.47 3.81 11.91C3.81 7.37 7.5 3.68 12.04 3.68C14.25 3.68 16.31 4.54 17.87 6.1C19.42 7.66 20.28 9.72 20.28 11.92C20.28 16.46 16.59 20.15 12.05 20.15ZM16.57 14.39C16.32 14.26 15.1 13.66 14.87 13.58C14.65 13.5 14.48 13.46 14.32 13.7C14.16 13.95 13.69 14.5 13.55 14.66C13.41 14.83 13.27 14.85 13.02 14.73C12.77 14.6 11.98 14.34 11.04 13.5C10.31 12.85 9.81 12.04 9.67 11.8C9.53 11.55 9.66 11.41 9.78 11.29C9.89 11.18 10.03 11 10.16 10.85C10.28 10.7 10.32 10.58 10.4 10.41C10.48 10.25 10.44 10.1 10.38 9.98C10.32 9.85 9.83 8.65 9.63 8.16C9.43 7.69 9.23 7.75 9.07 7.74C8.93 7.74 8.76 7.73 8.59 7.73C8.43 7.73 8.16 7.79 7.93 8.04C7.71 8.29 7.08 8.87 7.08 10.07C7.08 11.27 7.95 12.43 8.08 12.59C8.2 12.75 9.81 15.24 12.28 16.3C12.87 16.55 13.33 16.71 13.69 16.82C14.28 17.01 14.82 16.98 15.24 16.92C15.71 16.85 16.69 16.33 16.9 15.75C17.1 15.17 17.1 14.68 17.04 14.57C16.98 14.47 16.82 14.51 16.57 14.39Z" />
          </svg>
        )
      });
    }
    if (instagram) {
      list.push({
        id: 'ig',
        label: 'Instagram',
        url: instagram,
        bg: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
        color: '#FFFFFF',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
          </svg>
        )
      });
    }
    if (facebook) {
      list.push({
        id: 'fb',
        label: 'Facebook',
        url: facebook,
        bg: '#1877F2',
        color: '#FFFFFF',
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        )
      });
    }
    if (mapUrl) {
      list.push({
        id: 'maps',
        label: 'Google Maps',
        url: mapUrl,
        bg: '#FFFFFF',
        color: '#EA4335',
        border: '1px solid #E2E8F0',
        icon: (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" />
            <circle cx="12" cy="9" r="2.8" fill="#FFFFFF" />
          </svg>
        )
      });
    }
    if (phone) {
      list.push({
        id: 'call',
        label: 'Call',
        url: `tel:${phone}`,
        bg: '#FFFFFF',
        color: '#16192E',
        border: '1px solid #E2E8F0',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16192E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        )
      });
    }
    return list;
  }, [formattedWhatsAppNumber, instagram, facebook, mapUrl, phone]);

  // Global Reusable Powered By Trivana & Delivery Aggregators Section
  const renderTrivanaFooter = (customStyle = {}) => (
    <div
      style={{
        textAlign: 'center',
        paddingTop: '20px',
        paddingBottom: '28px',
        width: '100%',
        marginTop: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        ...customStyle
      }}
    >
      <div
        style={{
          background: '#FFFFFF',
          width: '100%',
          maxWidth: '720px',
          borderRadius: '18px',
          padding: '18px 20px',
          color: '#64748B',
          fontSize: '12px',
          fontWeight: '600',
          letterSpacing: '0.04em',
          boxShadow: '0 4px 20px rgba(22, 25, 46, 0.05)',
          border: '1px solid #F1F5F9'
        }}
      >
        {/* Payment intermediary disclaimer - shown on menu / products screen only, not on home page */}
        {activeScreen === 'products' && (
          <div
            style={{
              color: '#64748B',
              fontWeight: '500',
              fontSize: '11px',
              lineHeight: '1.5',
              margin: '0 auto 12px',
              maxWidth: '680px',
              textAlign: 'center'
            }}
          >
            • For your order, the business is responsible for all payments. Trivana Hospitality act only as intermediaries and do not handle any payments. In case of any issue, please contact the business owner directly.
          </div>
        )}

        {/* POWERED BY TRIVANA */}
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '800', letterSpacing: '0.08em', color: '#16192E' }}>
            POWERED BY{' '}
            <a href="https://trivanahospitality.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB', fontWeight: '800', textDecoration: 'none' }}>
              TRIVANA
            </a>
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <a href="https://trivanahospitality.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', padding: '4px 8px' }}>
              <img src={propTrivanaLogo || trivanaLogoDefault} alt="Trivana Hospitality" style={{ height: '26px', objectFit: 'contain' }} />
            </a>
          </div>
        </div>

        {/* DIVIDER */}
        <div style={{ height: '1px', backgroundColor: '#E2E8F0', margin: '12px auto 14px', width: '85%' }} />

        {/* DELIVERY AGGREGATORS & SHIPPING INTEGRATIONS */}
        <div style={{ width: '100%', overflow: 'hidden' }}>
          <div style={{ margin: '0 0 10px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 3px', fontSize: '11px', fontWeight: '800', letterSpacing: '0.06em', color: '#16192E', textTransform: 'uppercase' }}>
              HAVING JUST ONE OPTION ISN'T AN OPTION
            </p>
            <p style={{ margin: 0, fontSize: '10px', fontWeight: '600', color: '#64748B' }}>
              Delivering to 20,000+ Pin Codes across India with 20+ Integrated Logistics Partners
            </p>
          </div>

          <div className="courier-marquee-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflow: 'hidden' }}>
            {/* Track 1 */}
            <div className="courier-marquee-track">
              {[...Array(2)].map((_, loopIdx) => (
                <React.Fragment key={`row1-loop-${loopIdx}`}>
                  <a href="https://yelofast.com" target="_blank" rel="noopener noreferrer" className="courier-pill" style={{ background: '#18181B', color: '#FACC15', textDecoration: 'none' }}>
                    <span style={{ fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ fontSize: '13px' }}>📦</span>
                      <span>YeloFast Instant</span>
                    </span>
                  </a>
                  <div className="courier-pill" style={{ background: '#F26522', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '900', fontStyle: 'italic', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '13px', letterSpacing: '-2px', marginRight: '2px' }}>»»</span>
                      <span>XPRESSBEES</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#09090B', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '900', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#EF4444', fontSize: '13px', lineHeight: '1' }}>■</span>
                      <span>DELHIVERY</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#BE185D', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '13px' }}>⚡</span>
                      <span>Ecom Express</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#131921', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontWeight: '800' }}>amazon</span>
                      <span style={{ fontWeight: '400', opacity: 0.85, fontSize: '10px' }}>shipping</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#004B93', color: '#FFFFFF' }}>
                    <span style={{ fontWeight: '900', fontStyle: 'italic', letterSpacing: '0.05em', fontSize: '12px' }}>
                      BLUE DART
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#0F172A', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '900', fontStyle: 'italic', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span>DTDC</span>
                      <span style={{ color: '#EF4444', fontSize: '11px', fontStyle: 'normal' }}>■</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#F9570C', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ fontSize: '12px' }}>◥</span>
                      <span style={{ fontWeight: '800' }}>shadowfax</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#000000', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#FFFFFF' }}>⚡</span>
                      <span>blitz</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#0284C7', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '900', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#FACC15' }}>✦</span>
                      <span>GATI</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#C4161C', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>📮</span>
                      <span>Speed Post</span>
                    </span>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Track 2 Reverse */}
            <div className="courier-marquee-track-reverse">
              {[...Array(2)].map((_, loopIdx) => (
                <React.Fragment key={`row2-loop-${loopIdx}`}>
                  <div className="courier-pill" style={{ background: '#0066CC', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '800', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ fontStyle: 'italic', fontWeight: '900' }}>e</span>
                      <span>kart</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#DC2626', color: '#FFFFFF' }}>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.1' }}>
                      <span style={{ fontWeight: '800', fontSize: '12px', letterSpacing: '-0.02em' }}>aramex</span>
                      <span style={{ fontSize: '6.5px', fontWeight: '600', opacity: 0.9 }}>delivery unlimited</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#059669', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#A7F3D0' }}>⚡</span>
                      <span>dunzo</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#0066FF', color: '#FFFFFF' }}>
                    <span style={{ fontWeight: '800', fontSize: '13px', letterSpacing: '-0.03em' }}>
                      borzo
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#3B82F6', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span>LOADSHARE</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#7C3AED', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ color: '#C4B5FD' }}>★</span>
                      <span>smartr</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#334A6E', color: '#FFFFFF' }}>
                    <span style={{ fontWeight: '900', letterSpacing: '0.06em', fontSize: '11px' }}>
                      D<span style={{ color: '#93C5FD' }}>⊙</span>TZOT
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#0B4D75', color: '#FFFFFF' }}>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.1' }}>
                      <span style={{ fontWeight: '800', fontSize: '10px', letterSpacing: '0.08em' }}>KERRY INDEV</span>
                      <span style={{ fontSize: '6.5px', fontWeight: '600', opacity: 0.85 }}>EXPRESS</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#0D9488', color: '#FFFFFF' }}>
                    <span style={{ fontWeight: '900', fontSize: '11px', letterSpacing: '0.06em' }}>
                      PIK N DEL
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#4F46E5', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#C7D2FE' }}>◬</span>
                      <span>pidge</span>
                    </span>
                  </div>
                  <div className="courier-pill" style={{ background: '#9F1239', color: '#FFFFFF' }}>
                    <span style={{ fontSize: '11px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span>⮌</span>
                      <span>Ecom Reverse</span>
                    </span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Common Slide-Out Navigation Drawer
  const renderDrawer = () => {
    if (!drawerOpen) return null;
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(22, 25, 46, 0.65)',
          backdropFilter: 'blur(6px)',
          zIndex: 1000,
          display: 'flex'
        }}
        onClick={() => setDrawerOpen(false)}
      >
        <div
          style={{
            width: '82%',
            maxWidth: '310px',
            height: '100%',
            backgroundColor: '#FFFFFF',
            padding: '28px 22px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '8px 0 32px rgba(22, 25, 46, 0.25)',
            boxSizing: 'border-box'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.02em' }}>
              {storeName}
            </div>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#64748B' }}
            >
              <Icons.Close />
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              type="button"
              onClick={() => {
                navigateTo('home');
                setDrawerOpen(false);
              }}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: activeScreen === 'home' ? '#16192E' : '#F8FAFC',
                color: activeScreen === 'home' ? '#FFFFFF' : '#16192E',
                fontWeight: 700,
                fontSize: '14px',
                textAlign: 'left',
                cursor: 'pointer'
              }}
            >
              Home
            </button>
            <button
              type="button"
              onClick={() => {
                navigateTo('products');
                setDrawerOpen(false);
              }}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: activeScreen === 'products' ? '#16192E' : '#F8FAFC',
                color: activeScreen === 'products' ? '#FFFFFF' : '#16192E',
                fontWeight: 700,
                fontSize: '14px',
                textAlign: 'left',
                cursor: 'pointer'
              }}
            >
              All Products
            </button>
            <button
              type="button"
              onClick={() => {
                navigateTo('checkout');
                setDrawerOpen(false);
              }}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: activeScreen === 'checkout' ? '#16192E' : '#F8FAFC',
                color: activeScreen === 'checkout' ? '#FFFFFF' : '#16192E',
                fontWeight: 700,
                fontSize: '14px',
                textAlign: 'left',
                cursor: 'pointer'
              }}
            >
              Cart & Checkout ({totalCartCount})
            </button>
          </div>

          <div style={{ marginTop: 'auto', borderTop: '1px solid #E2E8F0', paddingTop: '18px' }}>
            <div style={{ fontSize: '12px', color: '#64748B', lineHeight: 1.6 }}>
              {phone && <div>📞 {phone}</div>}
              <div>📍 {restaurant?.city || 'Jaipur, Rajasthan, India'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ndt-theme-container" style={{ minHeight: '100vh', width: '100%', position: 'relative' }}>
      {/* ─────────────────────────────────────────────────────────────────────────────
          GLOBAL TYPOGRAPHY & KEYFRAMES INJECTION
         ───────────────────────────────────────────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,800;1,600;1,700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

        .ndt-theme-container,
        .ndt-theme-container * {
          font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          box-sizing: border-box;
        }

        .ndt-serif {
          font-family: 'Playfair Display', Georgia, serif !important;
        }

        @keyframes ndt-pulse-glow {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50% { transform: scale(1.15); opacity: 1; }
        }

        @keyframes ndt-float-soft {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }

        @keyframes ndt-sheet-slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .ndt-sheet-content {
          animation: ndt-sheet-slide-up 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        /* Responsive Layout & Desktop Compression */
        .ndt-content-wrapper {
          width: 100%;
          max-width: 1240px;
          margin: 0 auto;
          box-sizing: border-box;
        }

        .ndt-checkout-wrapper {
          width: 100%;
          max-width: 680px;
          margin: 0 auto;
          box-sizing: border-box;
        }

        .ndt-products-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          padding: 8px 16px;
          box-sizing: border-box;
        }

        @media (min-width: 640px) {
          .ndt-products-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 16px;
            padding: 12px 20px;
          }
        }

        @media (min-width: 1024px) {
          .ndt-products-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 18px;
            padding: 16px 24px;
          }
        }

        @media (min-width: 1440px) {
          .ndt-products-grid {
            grid-template-columns: repeat(5, minmax(0, 1fr));
            gap: 20px;
            padding: 20px 24px;
          }
        }

        .ndt-product-card-image {
          width: 100%;
          height: 135px;
          border-radius: 12px;
          background-color: #F8FAFC;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
        }

        @media (min-width: 640px) {
          .ndt-product-card-image {
            height: 145px;
          }
        }

        @media (min-width: 1024px) {
          .ndt-product-card-image {
            height: 155px;
          }
        }
      `}</style>

      {/* ─────────────────────────────────────────────────────────────────────────────
          PAGE 1: LOADING PAGE
         ───────────────────────────────────────────────────────────────────────────── */}
      {activeScreen === 'loading' && (
        <div
          style={{
            minHeight: '100vh',
            background: 'radial-gradient(circle at 50% 30%, #FFFFFF 0%, #FCFAF7 60%, #F6EFE6 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '48px 24px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Soft flowing pastel wavy ribbon lines in background */}
          <svg
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
            viewBox="0 0 400 800"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M-50,220 C80,180 220,340 450,260"
              stroke="rgba(212, 163, 115, 0.28)"
              strokeWidth="1.5"
              strokeDasharray="4 4"
            />
            <path
              d="M-20,380 C120,440 280,300 480,480"
              stroke="rgba(22, 25, 46, 0.14)"
              strokeWidth="1.5"
            />
            <path
              d="M20,680 C180,600 240,750 420,650"
              stroke="rgba(212, 163, 115, 0.22)"
              strokeWidth="2"
            />
          </svg>

          {/* Floating Golden & Slate Bokeh Circles */}
          <div style={{ position: 'absolute', top: '12%', left: '10%', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#D4A373', opacity: 0.6 }} />
          <div style={{ position: 'absolute', top: '22%', right: '14%', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#16192E', opacity: 0.35 }} />
          <div style={{ position: 'absolute', top: '46%', left: '14%', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#16192E', opacity: 0.25 }} />
          <div style={{ position: 'absolute', top: '54%', right: '16%', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#D4A373', opacity: 0.55 }} />
          <div style={{ position: 'absolute', bottom: '24%', left: '22%', width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#D4A373', opacity: 0.45 }} />
          <div style={{ position: 'absolute', bottom: '18%', right: '20%', width: '11px', height: '11px', borderRadius: '50%', backgroundColor: '#16192E', opacity: 0.3 }} />

          {/* Top Header Row with Skip Button */}
          <div style={{ width: '100%', maxWidth: '800px', display: 'flex', justifyContent: 'flex-end', zIndex: 10 }}>
            <button
              type="button"
              onClick={skipLoading}
              style={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(212, 163, 115, 0.45)',
                color: '#16192E',
                fontSize: '12px',
                fontWeight: 700,
                padding: '6px 14px',
                borderRadius: '9999px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <span>Skip</span>
              <span>→</span>
            </button>
          </div>

          {/* Center Emblem & Typography */}
          <div style={{ textAlign: 'center', maxWidth: '340px', width: '100%', zIndex: 2 }}>
            {/* Concentric Halo Rings with Luxury Brand Emblem */}
            <div
              style={{
                position: 'relative',
                width: '184px',
                height: '184px',
                margin: '0 auto 30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* Outer ambient gold glow aura */}
              <div
                style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(212, 163, 115, 0.25) 0%, transparent 70%)',
                  filter: 'blur(8px)'
                }}
              />

              {/* Outermost subtle thin ring with navy arc */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 184 184">
                <circle cx="92" cy="92" r="90" stroke="rgba(212, 163, 115, 0.35)" strokeWidth="1.5" fill="none" strokeDasharray="6 4" />
                <path d="M92,2 A90,90 0 0,1 182,92" stroke="#16192E" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              </svg>

              {/* Middle decorative gold border */}
              <div
                style={{
                  position: 'absolute',
                  inset: '16px',
                  borderRadius: '50%',
                  border: '1.5px solid rgba(212, 163, 115, 0.5)'
                }}
              />

              {/* Inner elevated pedestal with Store Emblem */}
              <div
                style={{
                  width: '124px',
                  height: '124px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  border: '2px solid rgba(212, 163, 115, 0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 16px 40px rgba(22, 25, 46, 0.12), 0 4px 14px rgba(212, 163, 115, 0.28)',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                {renderStoreEmblem(112, 48)}
              </div>
            </div>

            {/* Welcome Typography */}
            <div style={{ marginBottom: '6px' }}>
              <span style={{
                fontSize: '11.5px',
                fontWeight: 800,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: '#D4A373'
              }}>
                ✦ Welcome To ✦
              </span>
            </div>
            <h1
              className="ndt-serif"
              style={{
                fontSize: '32px',
                fontWeight: 800,
                color: '#16192E',
                margin: '0 0 8px',
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
            >
              {storeName}
            </h1>
            <p
              style={{
                fontSize: '13.5px',
                color: '#64748B',
                margin: 0,
                fontWeight: 500,
                lineHeight: 1.4,
                letterSpacing: '-0.01em'
              }}
            >
              {storeTagline || 'Authentic Taste • Delivered Fresh'}
            </p>
          </div>

          {/* Bottom Progress Bar Section */}
          <div style={{ width: '100%', maxWidth: '280px', textAlign: 'center', zIndex: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div
                style={{
                  flex: 1,
                  height: '6px',
                  backgroundColor: '#E2E8F0',
                  borderRadius: '9999px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${loadingProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #16192E 0%, #D4A373 100%)',
                    borderRadius: '9999px',
                    boxShadow: '0 0 10px rgba(212, 163, 115, 0.5)',
                    transition: 'width 0.12s ease'
                  }}
                />
              </div>
              <span style={{ fontSize: '12.5px', color: '#16192E', fontWeight: 800, minWidth: '34px', textAlign: 'right' }}>
                {loadingProgress}%
              </span>
            </div>
            <div style={{ fontSize: '11.5px', color: '#8A99AD', fontWeight: 600, letterSpacing: '0.02em' }}>
              Preparing your experience...
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          PAGE 2: HOME PAGE
         ───────────────────────────────────────────────────────────────────────────── */}
      {activeScreen === 'home' && (
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: '#FAF9F6',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflowX: 'hidden'
          }}
        >
          {/* Top Header */}
          <header
            style={{
              padding: '16px 20px',
              background: 'transparent',
              zIndex: 10
            }}
          >
            <div className="ndt-content-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', display: 'flex' }}
                aria-label="Menu"
              >
                <Icons.Hamburger />
              </button>

              <button
                type="button"
                onClick={() => navigateTo('checkout')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '6px', display: 'flex' }}
                aria-label="Cart"
              >
                <Icons.Cart color="#16192E" size={24} />
                {totalCartCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '0px',
                      right: '0px',
                      backgroundColor: '#D97706',
                      color: '#FFFFFF',
                      fontSize: '11px',
                      fontWeight: 800,
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1.5px solid #FFFFFF'
                    }}
                  >
                    {totalCartCount}
                  </span>
                )}
              </button>
            </div>
          </header>

          {/* Store Closed Banner */}
          {!restaurantOpen && (
            <div className="ndt-content-wrapper" style={{ padding: '0 20px 12px', zIndex: 10 }}>
              <div
                style={{
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#991B1B',
                  padding: '10px 16px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: 700,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>⚠️</span>
                <span>Store is currently closed for online orders. Browsing is open.</span>
              </div>
            </div>
          )}

          {/* Scattered Soft Bokeh Dots in Upper Background */}
          <div style={{ position: 'absolute', top: '15%', left: '8%', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#D4A373', opacity: 0.45, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '22%', right: '10%', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#16192E', opacity: 0.3, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '38%', right: '8%', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#D4A373', opacity: 0.4, pointerEvents: 'none' }} />

          {/* Hero Section */}
          <div className="ndt-content-wrapper" style={{ padding: '20px 24px 14px', textAlign: 'center', zIndex: 5, maxWidth: '800px', margin: '0 auto' }}>
            {/* Circular Store Avatar with Halo */}
            <div
              style={{
                width: '130px',
                height: '130px',
                margin: '0 auto 18px',
                borderRadius: '50%',
                backgroundColor: '#FFFFFF',
                border: '2px solid rgba(212, 163, 115, 0.65)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 14px 32px rgba(212, 163, 115, 0.24)'
              }}
            >
              {renderStoreEmblem(116, 46)}
            </div>

            <h2
              style={{
                fontSize: '24px',
                fontWeight: 800,
                color: '#16192E',
                margin: '0 0 6px',
                letterSpacing: '-0.025em'
              }}
            >
              {storeName}
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: '#64748B',
                margin: '0 0 24px',
                lineHeight: 1.5,
                maxWidth: '320px',
                marginInline: 'auto',
                fontWeight: 500,
                letterSpacing: '-0.01em'
              }}
            >
              {storeTagline}
            </p>

            {/* Social & Action Buttons (Configured only, no dead clicks) */}
            {socialLinks.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '28px',
                  flexWrap: 'wrap'
                }}
              >
                {socialLinks.map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', textAlign: 'center', width: '56px' }}
                  >
                    <div
                      style={{
                        width: '46px',
                        height: '46px',
                        borderRadius: '50%',
                        background: s.bg,
                        color: s.color || '#FFFFFF',
                        border: s.border || 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 6px',
                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      {s.icon}
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748B', fontWeight: 600 }}>{s.label}</span>
                  </a>
                ))}
              </div>
            )}

            {/* Primary CTA Button: Explore Our Products → */}
            <button
              type="button"
              onClick={() => navigateTo('products')}
              style={{
                width: '100%',
                maxWidth: '310px',
                padding: '16px 28px',
                backgroundColor: '#16192E',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '9999px',
                fontSize: '15px',
                fontWeight: 800,
                letterSpacing: '-0.01em',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: '0 10px 24px rgba(22, 25, 46, 0.28)',
                transition: 'transform 0.15s ease'
              }}
            >
              <span>Explore Our Products</span>
              <span style={{ fontSize: '18px', lineHeight: 1 }}>→</span>
            </button>
          </div>

          {/* 3 Trust Feature Badges */}
          <div
            className="ndt-content-wrapper"
            style={{
              padding: '16px 24px 20px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              textAlign: 'center',
              zIndex: 5,
              maxWidth: '680px',
              margin: '0 auto'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ marginBottom: '8px' }}>
                <Icons.ShieldCheck />
              </div>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#16192E', lineHeight: 1.3 }}>
                Quality<br />Products
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ marginBottom: '8px' }}>
                <Icons.Truck size={24} color="#16192E" />
              </div>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#16192E', lineHeight: 1.3 }}>
                Safe & Secure<br />Shopping
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ marginBottom: '8px' }}>
                <Icons.Headset />
              </div>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#16192E', lineHeight: 1.3 }}>
                Customer<br />Support
              </span>
            </div>
          </div>

          {/* ─────────────────────────────────────────────────────────────────────────────
              ENHANCED ORGANIC WAVES (High-Fidelity, Tall & Dramatic Curves with Glowing Orbs)
             ───────────────────────────────────────────────────────────────────────────── */}
          <div
            style={{
              marginTop: 'auto',
              position: 'relative',
              width: '100%',
              minHeight: '220px',
              height: '240px',
              overflow: 'hidden',
              lineHeight: 0
            }}
          >
            <svg
              viewBox="0 0 1440 380"
              preserveAspectRatio="none"
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                minHeight: '220px'
              }}
            >
              <defs>
                {/* Golden sand gradient */}
                <linearGradient id="waveSandGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ECC8A2" />
                  <stop offset="50%" stopColor="#E5B887" />
                  <stop offset="100%" stopColor="#D4A373" />
                </linearGradient>

                {/* Slate denim gradient */}
                <linearGradient id="waveSlateGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3A4861" />
                  <stop offset="100%" stopColor="#283347" />
                </linearGradient>

                {/* Deep midnight navy gradient */}
                <linearGradient id="waveNavyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1C2138" />
                  <stop offset="100%" stopColor="#141729" />
                </linearGradient>

                {/* Glow filter for orbs */}
                <filter id="glowGoldOrb" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>

              {/* Layer 1: Golden Sand Wave (Rising into rolling dune on the right) */}
              <path
                fill="url(#waveSandGrad)"
                d="M0,190 C240,210 420,285 700,240 C980,185 1120,70 1320,110 C1380,122 1420,140 1440,155 L1440,380 L0,380 Z"
              />

              {/* Layer 2: Slate Navy Mid-Wave (Graceful crest in center-left) */}
              <path
                fill="url(#waveSlateGrad)"
                d="M0,240 C180,220 360,135 600,130 C860,125 1020,230 1220,245 C1320,255 1390,240 1440,220 L1440,380 L0,380 Z"
              />

              {/* Layer 3: Foreground Deep Midnight Navy Wave (Dramatic rolling curve across screen) */}
              <path
                fill="url(#waveNavyGrad)"
                d="M0,130 C160,150 360,210 620,215 C880,220 1060,290 1260,270 C1360,260 1410,210 1440,170 L1440,380 L0,380 Z"
              />

              {/* Glowing Orb 1: Radiant Golden Moon/Orb */}
              <g filter="url(#glowGoldOrb)">
                <circle cx="620" cy="270" r="10" fill="#E5B887" />
              </g>
              <circle cx="620" cy="270" r="18" fill="#E5B887" opacity="0.28" />

              {/* Glowing Orb 2: Crisp Sparkling Star/Pearl */}
              <circle cx="960" cy="295" r="5" fill="#FFFFFF" opacity="0.9" />
              <circle cx="960" cy="295" r="11" fill="#FFFFFF" opacity="0.22" />

              {/* Glowing Orb 3: Subtle floating speck */}
              <circle cx="340" cy="290" r="4" fill="#D4A373" opacity="0.65" />
            </svg>
          </div>

          {/* Powered by Trivana & Delivery Logistics Partner Marquee - Below the Waves Design at the very bottom */}
          <div
            style={{
              backgroundColor: '#141729',
              width: '100%',
              padding: '0 16px 28px',
              marginTop: '-1px',
              boxSizing: 'border-box',
              zIndex: 6
            }}
          >
            <div className="ndt-content-wrapper" style={{ padding: 0 }}>
              {renderTrivanaFooter({ marginTop: '0px', paddingTop: '16px', paddingBottom: '16px' })}
            </div>
          </div>

          {/* Global Drawer */}
          {renderDrawer()}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          PAGE 3: PRODUCTS PAGE
         ───────────────────────────────────────────────────────────────────────────── */}
      {activeScreen === 'products' && (
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: totalCartCount > 0 ? '90px' : '36px'
          }}
        >
          {/* Products Top Header */}
          <header
            style={{
              padding: '14px 20px',
              position: 'sticky',
              top: 0,
              backgroundColor: '#FFFFFF',
              zIndex: 30,
              borderBottom: '1px solid #F1F5F9'
            }}
          >
            <div
              className="ndt-content-wrapper"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 4px'
              }}
            >
              <button
                type="button"
                onClick={() => navigateTo('home')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
                aria-label="Back to home"
              >
                <Icons.Back />
              </button>

              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.02em' }}>
                All Products
              </h2>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById('ndt-search-input');
                    if (el) el.focus();
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
                  aria-label="Search"
                >
                  <Icons.Search color="#16192E" size={22} />
                </button>

                <button
                  type="button"
                  onClick={() => navigateTo('checkout')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '4px', display: 'flex' }}
                  aria-label="Cart"
                >
                  <Icons.Cart color="#16192E" size={24} />
                  {totalCartCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-4px',
                        backgroundColor: '#D97706',
                        color: '#FFFFFF',
                        fontSize: '11px',
                        fontWeight: 800,
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1.5px solid #FFFFFF'
                      }}
                    >
                      {totalCartCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </header>

          {/* Store Closed Warning Banner */}
          {!restaurantOpen && (
            <div className="ndt-content-wrapper" style={{ padding: '12px 16px 0', zIndex: 10 }}>
              <div
                style={{
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#991B1B',
                  padding: '10px 16px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: 700,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>⚠️</span>
                <span>Store is currently closed for online orders. Browsing is open.</span>
              </div>
            </div>
          )}

          {/* Inner Content Wrapper for Desktop Compression */}
          <div className="ndt-content-wrapper">
            {/* Live Search Bar + Filter Button Row */}
            <div style={{ padding: '14px 16px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Search Input Box */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '9999px',
                  padding: '10px 16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                }}
              >
                <Icons.Search size={18} color="#94A3B8" />
                <input
                  id="ndt-search-input"
                  type="text"
                  placeholder="Search for products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontSize: '14px',
                    color: '#16192E',
                    width: '100%',
                    fontWeight: 500
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 0 }}
                  >
                    <Icons.Close />
                  </button>
                )}
              </div>

              {/* Filter Toggle Button */}
              <button
                type="button"
                onClick={() => setFilterModalOpen(true)}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  backgroundColor: activeFiltersCount > 0 ? '#16192E' : '#FFFFFF',
                  border: activeFiltersCount > 0 ? '1px solid #16192E' : '1px solid #E2E8F0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  position: 'relative',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                }}
                aria-label="Filter"
              >
                <Icons.Filter color={activeFiltersCount > 0 ? '#FFFFFF' : '#16192E'} />
                {activeFiltersCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: '#EF4444',
                      color: '#FFFFFF',
                      fontSize: '10px',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #FFFFFF'
                    }}
                  >
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Category Horizontal Filter Chips with Desktop Scroll Controls */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
            {!isMobile && (
              <button
                type="button"
                onClick={() => scrollCategories(-220)}
                style={{
                  position: 'absolute',
                  left: '6px',
                  zIndex: 10,
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: '15px',
                  color: '#16192E'
                }}
                aria-label="Scroll left"
              >
                ‹
              </button>
            )}

            <div
              ref={categoryScrollRef}
              style={{
                padding: '8px 20px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                scrollBehavior: 'smooth',
                width: '100%'
              }}
            >
              {[
                { id: 'ALL', label: 'All' },
                ...(wishlist.size > 0 ? [{ id: 'WISHLIST', label: `❤️ Wishlist (${wishlist.size})` }] : []),
                { id: 'OFFERS', label: 'Offers' },
                ...catalogCategories
              ].map((cat) => {
                const isActive = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.id)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: '9999px',
                      border: 'none',
                      backgroundColor: isActive ? '#16192E' : '#F1F5F9',
                      color: isActive ? '#FFFFFF' : '#475569',
                      fontSize: '13px',
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {!isMobile && (
              <button
                type="button"
                onClick={() => scrollCategories(220)}
                style={{
                  position: 'absolute',
                  right: '6px',
                  zIndex: 10,
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: '15px',
                  color: '#16192E'
                }}
                aria-label="Scroll right"
              >
                ›
              </button>
            )}
          </div>

          {/* Products Responsive Compressed Grid */}
          <div className="ndt-products-grid">
            {filteredProducts.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px 16px', color: '#64748B' }}>
                <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 8px', color: '#16192E' }}>No products found</p>
                <p style={{ fontSize: '13px', margin: 0 }}>Try adjusting your search terms or filters</p>
              </div>
            ) : (
              filteredProducts.map((p) => {
                const pId = p.id || p.productId;
                const qty = getItemQuantity(pId);
                const pricingInfo = getProductPricingInfo(p, getProductDisplayPriceMeta);
                const title = p.productName || p.name || 'Product';
                const isFav = wishlist.has(pId);
                const isCustomizable = hasCustomizations(p);
                const isVeg = isItemVeg(p);
                const outOfStock = isItemOutOfStock(p);

                return (
                  <div
                    key={pId}
                    onClick={() => setSelectedDetailProduct(p)}
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #F1F5F9',
                      borderRadius: '16px',
                      padding: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      position: 'relative',
                      cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03)',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      opacity: outOfStock ? 0.8 : 1
                    }}
                  >
                    {/* Top Discount Badge */}
                    {pricingInfo.hasDiscount && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '10px',
                          left: '10px',
                          background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                          color: '#FFFFFF',
                          fontSize: '10px',
                          fontWeight: 800,
                          letterSpacing: '0.03em',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(220, 38, 38, 0.35)',
                          zIndex: 2,
                          textTransform: 'uppercase'
                        }}
                      >
                        {pricingInfo.discountPercent}% OFF
                      </div>
                    )}

                    {/* Top Wishlist Heart */}
                    <button
                      type="button"
                      onClick={(e) => toggleWishlist(pId, e)}
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(4px)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 2
                      }}
                      aria-label="Wishlist"
                    >
                      <Icons.Heart filled={isFav} size={15} />
                    </button>

                    {/* Product Image */}
                    <div
                      className="ndt-product-card-image"
                      style={{
                        flexShrink: 0,
                        position: 'relative'
                      }}
                    >
                      {p.imageUrl ? (
                        <img
                          src={resolveImageUrl(p.imageUrl)}
                          alt={title}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{ color: '#94A3B8', fontSize: '32px' }}>🛍️</div>
                      )}

                      {/* Out of Stock Pill on Image */}
                      {outOfStock && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '8px',
                            left: '8px',
                            backgroundColor: 'rgba(15, 23, 42, 0.85)',
                            backdropFilter: 'blur(4px)',
                            color: '#FFFFFF',
                            fontSize: '9.5px',
                            fontWeight: 800,
                            letterSpacing: '0.04em',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            zIndex: 3,
                            textTransform: 'uppercase'
                          }}
                        >
                          Out of Stock
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div>
                      {/* Veg/Non-veg indicator + Title */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
                        <div
                          style={{
                            width: '13px',
                            height: '13px',
                            border: `1.5px solid ${isVeg ? '#16A34A' : '#DC2626'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '3px',
                            backgroundColor: '#FFFFFF',
                            flexShrink: 0,
                            marginTop: '2px'
                          }}
                        >
                          <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: isVeg ? '#16A34A' : '#DC2626' }} />
                        </div>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: '13px',
                            fontWeight: 700,
                            color: '#16192E',
                            lineHeight: 1.35,
                            letterSpacing: '-0.015em',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            minHeight: '34px'
                          }}
                        >
                          {title}
                        </h4>
                      </div>

                      {/* Pricing Row: Selling price + Strikethrough MRP + % OFF */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        <span style={{ fontSize: '15px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.02em' }}>
                          {pricingInfo.isEstimated ? 'From ' : ''}{formatPrice(pricingInfo.sellingPrice)}
                        </span>
                        {pricingInfo.hasDiscount && (
                          <>
                            <span style={{ fontSize: '12px', color: '#94A3B8', textDecoration: 'line-through', fontWeight: 600 }}>
                              {formatPrice(pricingInfo.mrp)}
                            </span>
                            <span
                              style={{
                                fontSize: '10.5px',
                                fontWeight: 800,
                                color: '#16A34A',
                                backgroundColor: '#DCFCE7',
                                padding: '1px 5px',
                                borderRadius: '4px',
                                lineHeight: 1.3
                              }}
                            >
                              {pricingInfo.discountPercent}% OFF
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Add / Stepper Button */}
                    <div onClick={(e) => e.stopPropagation()}>
                      {outOfStock ? (
                        <button
                          type="button"
                          disabled
                          style={{
                            width: '100%',
                            padding: '8px 14px',
                            backgroundColor: '#F1F5F9',
                            color: '#94A3B8',
                            border: '1px solid #E2E8F0',
                            borderRadius: '9999px',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <span>Out of Stock</span>
                        </button>
                      ) : qty > 0 && !isCustomizable ? (
                        <div
                          style={{
                            width: '100%',
                            padding: '6px 12px',
                            backgroundColor: '#16192E',
                            color: '#FFFFFF',
                            borderRadius: '9999px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontWeight: 800,
                            fontSize: '13px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleProductQuantityChange(p, -1)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#FFFFFF',
                              fontSize: '16px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              padding: '0 6px'
                            }}
                          >
                            −
                          </button>
                          <span>{qty}</span>
                          <button
                            type="button"
                            onClick={() => handleProductQuantityChange(p, 1)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#FFFFFF',
                              fontSize: '16px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              padding: '0 6px'
                            }}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAddToCart(p)}
                          style={{
                            width: '100%',
                            padding: '8px 14px',
                            backgroundColor: '#16192E',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '9999px',
                            fontSize: '13px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            transition: 'background 0.15s ease'
                          }}
                        >
                          <span>{qty > 0 && isCustomizable ? `+ Add (${qty})` : '+ Add'}</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          </div>

          {/* Global Reusable Powered by Trivana & Delivery Aggregators Section */}
          <div className="ndt-content-wrapper" style={{ padding: '16px 16px 24px' }}>
            {renderTrivanaFooter()}
          </div>

          {/* Floating Cart Button at bottom if cart has items */}
          {totalCartCount > 0 && (
            <div
              style={{
                position: 'fixed',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 'min(92%, 460px)',
                backgroundColor: '#16192E',
                color: '#FFFFFF',
                borderRadius: '9999px',
                padding: '12px 22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 12px 28px rgba(22, 25, 46, 0.35)',
                zIndex: 40,
                cursor: 'pointer'
              }}
              onClick={() => navigateTo('checkout')}
            >
              <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                {deliverySlabMessage ? (
                  <>
                    <div style={{
                      fontSize: '12.5px',
                      fontWeight: 800,
                      color: '#38BDF8',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.25,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {deliverySlabMessage}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>
                      Total: {formatPrice(pricing.grandTotal)} • {totalCartCount} item{totalCartCount > 1 ? 's' : ''}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '-0.01em' }}>
                      {totalCartCount} item{totalCartCount > 1 ? 's' : ''} added
                    </div>
                    <div style={{ fontSize: '12px', color: '#CBD5E1' }}>
                      Total: {formatPrice(pricing.grandTotal)}
                    </div>
                  </>
                )}
              </div>
              <div style={{ fontWeight: 800, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                <span>View Cart</span>
                <span>→</span>
              </div>
            </div>
          )}

          {/* ─────────────────────────────────────────────────────────────────────────────
              LUXURY FILTER & SORT POP-UP MODAL (Enhanced Apple/iOS Bottom Sheet UI)
             ───────────────────────────────────────────────────────────────────────────── */}
          {filterModalOpen && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.62)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                zIndex: 1000,
                display: 'flex',
                alignItems: isMobile ? 'flex-end' : 'center',
                justifyContent: 'center',
                padding: isMobile ? 0 : '20px'
              }}
              onClick={() => setFilterModalOpen(false)}
            >
              <div
                className="ndt-sheet-content"
                style={{
                  width: '100%',
                  maxWidth: '480px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: isMobile ? '28px 28px 0 0' : '24px',
                  padding: isMobile ? '16px 22px 28px' : '24px 26px 28px',
                  maxHeight: isMobile ? '84vh' : '88vh',
                  overflowY: 'auto',
                  boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.25)',
                  position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Drag Handle Notch */}
                {isMobile && (
                  <div
                    style={{
                      width: '42px',
                      height: '5px',
                      backgroundColor: '#E2E8F0',
                      borderRadius: '9999px',
                      margin: '0 auto 16px',
                      cursor: 'grab'
                    }}
                  />
                )}

                {/* Modal Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '19px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.02em' }}>
                      Filters & Sort
                    </h3>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748B', fontWeight: 500 }}>
                      Personalize your product selection
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFilterModalOpen(false)}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#F1F5F9',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#64748B'
                    }}
                    aria-label="Close filters"
                  >
                    <Icons.Close />
                  </button>
                </div>

                {/* 1. Food Preference Filter */}
                <div style={{ marginBottom: '22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.01em' }}>
                      Food Preference
                    </span>
                    {vegFilter !== 'ALL' && (
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#2563EB', cursor: 'pointer' }} onClick={() => setVegFilter('ALL')}>
                        Reset
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {[
                      { id: 'ALL', label: 'All Items', icon: '🍽️' },
                      { id: 'VEG', label: 'Veg Only', icon: '🟢' },
                      { id: 'NON_VEG', label: 'Non-Veg', icon: '🔴' }
                    ].map((f) => {
                      const isSel = vegFilter === f.id;
                      return (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setVegFilter(f.id)}
                          style={{
                            padding: '12px 8px',
                            borderRadius: '14px',
                            border: isSel ? '2px solid #16192E' : '1px solid #E2E8F0',
                            backgroundColor: isSel ? '#F8FAFC' : '#FFFFFF',
                            fontWeight: 700,
                            fontSize: '12px',
                            color: '#16192E',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <span style={{ fontSize: '14px' }}>{f.icon}</span>
                          <span>{f.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 2. Sort By Price */}
                <div style={{ marginBottom: '22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.01em' }}>
                      Sort By Price
                    </span>
                    {sortBy !== 'DEFAULT' && (
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#2563EB', cursor: 'pointer' }} onClick={() => setSortBy('DEFAULT')}>
                        Reset
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {[
                      { id: 'DEFAULT', label: 'Default', icon: <Icons.Sparkles /> },
                      { id: 'PRICE_LOW', label: 'Low to High', icon: <Icons.ArrowUp /> },
                      { id: 'PRICE_HIGH', label: 'High to Low', icon: <Icons.ArrowDown /> }
                    ].map((s) => {
                      const isSel = sortBy === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setSortBy(s.id)}
                          style={{
                            padding: '12px 8px',
                            borderRadius: '14px',
                            border: isSel ? '2px solid #16192E' : '1px solid #E2E8F0',
                            backgroundColor: isSel ? '#16192E' : '#FFFFFF',
                            fontWeight: 700,
                            fontSize: '12px',
                            color: isSel ? '#FFFFFF' : '#16192E',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <span style={{ color: isSel ? '#FFFFFF' : '#16192E' }}>{s.icon}</span>
                          <span>{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 3. In-Stock Items Only Toggle (iOS Style Switch) */}
                <div
                  style={{
                    marginBottom: '26px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 16px',
                    backgroundColor: '#F8FAFC',
                    borderRadius: '14px',
                    border: '1px solid #E2E8F0'
                  }}
                >
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#16192E' }}>
                      In-Stock Items Only
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748B', fontWeight: 500, marginTop: '2px' }}>
                      Hide out-of-stock products
                    </div>
                  </div>

                  {/* iOS Style Sliding Switch */}
                  <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={inStockOnly}
                      onChange={(e) => setInStockOnly(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: inStockOnly ? '#16192E' : '#CBD5E1',
                        borderRadius: '9999px',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '3px',
                          left: inStockOnly ? '25px' : '3px',
                          width: '20px',
                          height: '20px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '50%',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          transition: 'left 0.2s ease'
                        }}
                      />
                    </span>
                  </label>
                </div>

                {/* Bottom Action Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setVegFilter('ALL');
                      setSortBy('DEFAULT');
                      setInStockOnly(false);
                      setFilterModalOpen(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      borderRadius: '9999px',
                      border: '1px solid #CBD5E1',
                      backgroundColor: '#FFFFFF',
                      color: '#64748B',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Reset All
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilterModalOpen(false)}
                    style={{
                      flex: 2,
                      padding: '14px',
                      borderRadius: '9999px',
                      border: 'none',
                      backgroundColor: '#16192E',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      boxShadow: '0 8px 20px rgba(22, 25, 46, 0.25)'
                    }}
                  >
                    Show {filteredProducts.length} Products →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Global Drawer */}
          {renderDrawer()}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          PAGE 4: CHECKOUT / CART PAGE
         ───────────────────────────────────────────────────────────────────────────── */}
      {activeScreen === 'checkout' && (
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: '40px'
          }}
        >
          {/* Checkout Top Header */}
          <header
            style={{
              padding: '14px 20px',
              position: 'sticky',
              top: 0,
              backgroundColor: '#FFFFFF',
              zIndex: 30,
              borderBottom: '1px solid #F1F5F9'
            }}
          >
            <div className="ndt-checkout-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
              <button
                type="button"
                onClick={() => navigateTo(previousScreen || 'products')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
                aria-label="Back"
              >
                <Icons.Back />
              </button>

              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.02em' }}>
                Your Cart ({totalCartCount})
              </h2>

              {displayCart.length > 0 ? (
                <button
                  type="button"
                  onClick={handleClearCart}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
                  aria-label="Clear cart"
                >
                  <Icons.Trash color="#16192E" size={20} />
                </button>
              ) : (
                <div style={{ width: '24px' }} />
              )}
            </div>
          </header>

          {/* Cart Items List */}
          <div className="ndt-checkout-wrapper" style={{ padding: '16px 20px' }}>
            {displayCart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748B' }}>
                <div style={{ fontSize: '42px', marginBottom: '12px' }}>🛒</div>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 800, color: '#16192E' }}>
                  Your cart is empty
                </h3>
                <p style={{ margin: '0 0 20px', fontSize: '14px' }}>
                  Explore our catalog and add your favorite items!
                </p>
                <button
                  type="button"
                  onClick={() => navigateTo('products')}
                  style={{
                    padding: '12px 28px',
                    borderRadius: '9999px',
                    border: 'none',
                    backgroundColor: '#16192E',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Browse Products →
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                {displayCart.map((item) => {
                  const itemId = item.cartItemId || item.id || item.productId;
                  const title = item.productName || item.name || 'Product';
                  const itemPricing = item.pricing || getProductPricingInfo(item, getProductDisplayPriceMeta);
                  const basePrice = Number(itemPricing.sellingPrice || item.discountPrice || item.price || 0);
                  const addonsPrice = (item.selectedAddons || []).reduce((sum, a) => sum + Number(a.addonPrice || 0), 0);
                  const effectivePrice = basePrice + addonsPrice;
                  const qty = Number(item.quantity || 1);
                  const note = itemNotes[itemId] || item.specialInstructions || '';

                  return (
                    <div
                      key={itemId}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        paddingBottom: '16px',
                        borderBottom: '1px solid #F1F5F9'
                      }}
                    >
                      {/* Thumbnail */}
                      <div
                        style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '12px',
                          backgroundColor: '#F8FAFC',
                          overflow: 'hidden',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {item.imageUrl ? (
                          <img src={resolveImageUrl(item.imageUrl)} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: '24px' }}>🛍️</span>
                        )}
                      </div>

                      {/* Info + Note */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#16192E', marginBottom: '4px', letterSpacing: '-0.015em' }}>
                          {title}
                        </div>

                        {/* Selected Addons Badges */}
                        {Array.isArray(item.selectedAddons) && item.selectedAddons.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '5px' }}>
                            {item.selectedAddons.map((addon, aIdx) => (
                              <span
                                key={aIdx}
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: '#475569',
                                  backgroundColor: '#F1F5F9',
                                  padding: '2px 6px',
                                  borderRadius: '4px'
                                }}
                              >
                                {addon.addonName || addon.optionName} {Number(addon.addonPrice || 0) > 0 ? `(+${formatPrice(addon.addonPrice)})` : ''}
                              </span>
                            ))}
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '14px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.02em' }}>
                            {formatPrice(effectivePrice)}
                          </span>
                          {itemPricing.hasDiscount && (
                            <>
                              <span style={{ fontSize: '12px', color: '#94A3B8', textDecoration: 'line-through', fontWeight: 600 }}>
                                {formatPrice(itemPricing.mrp)}
                              </span>
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 800,
                                  color: '#16A34A',
                                  backgroundColor: '#DCFCE7',
                                  padding: '1px 5px',
                                  borderRadius: '4px'
                                }}
                              >
                                {itemPricing.discountPercent}% OFF
                              </span>
                            </>
                          )}
                        </div>

                        {/* Add note link / input (Exact to Picture) */}
                        {editingNoteItemId === itemId ? (
                          <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                            <input
                              type="text"
                              placeholder="Special instructions for item..."
                              value={note}
                              onChange={(e) => setItemNotes((prev) => ({ ...prev, [itemId]: e.target.value }))}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #CBD5E1',
                                fontSize: '12px',
                                width: '100%'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveItemNote(itemId, note)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: '#16192E',
                                color: '#FFFFFF',
                                fontSize: '11px',
                                fontWeight: 700,
                                cursor: 'pointer'
                              }}
                            >
                              Save
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditingNoteItemId(itemId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              fontSize: '12px',
                              color: '#64748B',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              cursor: 'pointer',
                              fontWeight: 500
                            }}
                          >
                            <Icons.Note />
                            <span>{note ? `Note: "${note}"` : 'Add a note (Optional)'}</span>
                          </button>
                        )}
                      </div>

                      {/* Individual Trash & Quantity Stepper */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => handleRemoveCartItem(item)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#94A3B8' }}
                          aria-label="Remove item"
                        >
                          <Icons.Trash size={16} color="#94A3B8" />
                        </button>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#F8FAFC',
                            borderRadius: '9999px',
                            border: '1px solid #E2E8F0',
                            padding: '4px 10px'
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleCartItemQuantityChange(item, -1)}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 800, fontSize: '13px', color: '#16192E', padding: 0 }}
                          >
                            −
                          </button>
                          <span style={{ fontSize: '12px', fontWeight: 800, color: '#16192E', minWidth: '14px', textAlign: 'center' }}>
                            {qty}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCartItemQuantityChange(item, 1)}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 800, fontSize: '13px', color: '#16192E', padding: 0 }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Delivery Options Selector (Delivery, Takeaway, Drive Thru) */}
            {displayCart.length > 0 && (
              <>
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.02em' }}>
                    Delivery Options
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {/* 1. Delivery (Active by default with checkmark dot in upper-right) */}
                    <div
                      onClick={() => handleSelectDeliveryOption('DELIVERY')}
                      style={{
                        border: deliveryOption === 'DELIVERY' ? '2px solid #16192E' : '1px solid #E2E8F0',
                        borderRadius: '14px',
                        padding: '14px 10px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        backgroundColor: deliveryOption === 'DELIVERY' ? '#F8FAFC' : '#FFFFFF'
                      }}
                    >
                      {deliveryOption === 'DELIVERY' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: '#16192E',
                            color: '#FFFFFF',
                            fontSize: '9px',
                            fontWeight: 900,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ✓
                        </div>
                      )}
                      <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                        <Icons.Truck size={22} color="#16192E" />
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#16192E' }}>Delivery</span>
                    </div>

                    {/* 2. Takeaway */}
                    <div
                      onClick={() => handleSelectDeliveryOption('TAKEAWAY')}
                      style={{
                        border: deliveryOption === 'TAKEAWAY' ? '2px solid #16192E' : '1px solid #E2E8F0',
                        borderRadius: '14px',
                        padding: '14px 10px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        backgroundColor: deliveryOption === 'TAKEAWAY' ? '#F8FAFC' : '#FFFFFF'
                      }}
                    >
                      {deliveryOption === 'TAKEAWAY' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: '#16192E',
                            color: '#FFFFFF',
                            fontSize: '9px',
                            fontWeight: 900,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ✓
                        </div>
                      )}
                      <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                        <Icons.TakeawayBag size={22} color="#16192E" />
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#16192E' }}>Takeaway</span>
                    </div>

                    {/* 3. Drive Thru */}
                    <div
                      onClick={() => handleSelectDeliveryOption('DRIVE_THRU')}
                      style={{
                        border: deliveryOption === 'DRIVE_THRU' ? '2px solid #16192E' : '1px solid #E2E8F0',
                        borderRadius: '14px',
                        padding: '14px 10px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        backgroundColor: deliveryOption === 'DRIVE_THRU' ? '#F8FAFC' : '#FFFFFF'
                      }}
                    >
                      {deliveryOption === 'DRIVE_THRU' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: '#16192E',
                            color: '#FFFFFF',
                            fontSize: '9px',
                            fontWeight: 900,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ✓
                        </div>
                      )}
                      <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                        <Icons.DriveThruCar size={22} color="#16192E" />
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#16192E' }}>Drive Thru</span>
                    </div>
                  </div>
                </div>

                {/* Delivery Address Box */}
                <div
                  style={{
                    border: '1px solid #E2E8F0',
                    borderRadius: '14px',
                    padding: '14px 16px',
                    marginBottom: '14px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}
                >
                  <div style={{ marginTop: '2px' }}><Icons.LocationPin /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 800, color: '#16192E' }}>
                        {deliveryOption === 'DELIVERY' ? 'Delivery Address' : 'Pickup Store Address'}
                      </span>
                      {deliveryOption === 'DELIVERY' && (
                        <button
                          type="button"
                          onClick={() => {
                            if (openCheckoutModal) {
                              openCheckoutModal();
                            } else {
                              setEditingAddress(!editingAddress);
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#2563EB',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          Change
                        </button>
                      )}
                    </div>

                    {editingAddress ? (
                      <textarea
                        rows={2}
                        value={checkoutForm?.addressLine || customAddress}
                        onChange={(e) => {
                          setCustomAddress(e.target.value);
                          if (setCheckoutForm) {
                            setCheckoutForm((prev) => ({ ...prev, addressLine: e.target.value }));
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: '8px',
                          border: '1px solid #CBD5E1',
                          fontSize: '12px',
                          color: '#16192E',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748B', lineHeight: 1.5 }}>
                        {checkoutForm?.addressLine || customAddress}
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Instructions (Row with Chevron Right) */}
                <div
                  style={{
                    border: '1px solid #E2E8F0',
                    borderRadius: '14px',
                    padding: '14px 16px',
                    marginBottom: '18px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icons.Note />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#16192E' }}>
                        Additional Instructions (Optional)
                      </span>
                    </div>
                    <Icons.ChevronRight />
                  </div>
                  <input
                    type="text"
                    placeholder="E.g. Ring the bell twice, leave at door..."
                    value={orderInstructions}
                    onChange={(e) => {
                      setOrderInstructions(e.target.value);
                      if (setCheckoutForm) {
                        setCheckoutForm((prev) => ({ ...prev, deliveryInstructions: e.target.value }));
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      fontSize: '12px',
                      color: '#16192E',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Promo / Coupon Code Section */}
                <div
                  style={{
                    border: '1px solid #E2E8F0',
                    borderRadius: '14px',
                    padding: '14px 16px',
                    marginBottom: '20px',
                    backgroundColor: '#F8FAFC'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '15px' }}>🏷️</span>
                      <span style={{ fontSize: '13px', fontWeight: 800, color: '#16192E' }}>
                        Apply Coupon
                      </span>
                    </div>
                    {pricing.discountAmount > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (handleRemoveCoupon) {
                            handleRemoveCoupon();
                          } else if (handleApplyCoupon) {
                            handleApplyCoupon('');
                          }
                          if (setCouponCode) setCouponCode('');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#DC2626',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="ENTER COUPON CODE"
                      value={couponCode || ''}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        if (setCouponCode) setCouponCode(val);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: '1px solid #CBD5E1',
                        fontSize: '13px',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        backgroundColor: '#FFFFFF'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (handleApplyCoupon && couponCode) {
                          handleApplyCoupon(couponCode);
                        }
                      }}
                      disabled={!couponCode || couponsLoading}
                      style={{
                        padding: '10px 18px',
                        backgroundColor: couponCode ? '#16192E' : '#94A3B8',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: 800,
                        cursor: couponCode ? 'pointer' : 'not-allowed',
                        transition: 'background-color 0.15s ease'
                      }}
                    >
                      {couponsLoading ? '...' : 'Apply'}
                    </button>
                  </div>

                  {/* View Available Coupons Toggle */}
                  <div style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      onClick={async () => {
                        const nextState = !showCouponList;
                        setShowCouponList(nextState);
                        if (nextState && (!availableCoupons || availableCoupons.length === 0) && fetchAvailableCoupons) {
                          await fetchAvailableCoupons();
                        } else if (nextState && handleToggleCoupons) {
                          handleToggleCoupons();
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#2563EB',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      <span>🏷️</span>
                      <span>{showCouponList ? '▲ Hide Available Coupons' : 'View Available Coupons'}</span>
                      {availableCoupons && availableCoupons.length > 0 && (
                        <span style={{
                          backgroundColor: '#EFF6FF',
                          color: '#2563EB',
                          fontSize: '10.5px',
                          fontWeight: 800,
                          padding: '1px 6px',
                          borderRadius: '999px',
                          marginLeft: '2px'
                        }}>
                          {availableCoupons.length}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Available Coupons Expandable List */}
                  {showCouponList && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {couponsLoading && (
                        <div style={{ fontSize: '12px', color: '#64748B', padding: '6px 0' }}>
                          Loading available coupons...
                        </div>
                      )}
                      {!couponsLoading && (!availableCoupons || availableCoupons.length === 0) && (
                        <div style={{ fontSize: '12px', color: '#64748B', padding: '6px 0', fontStyle: 'italic' }}>
                          No active coupons available right now.
                        </div>
                      )}
                      {!couponsLoading && availableCoupons && availableCoupons.length > 0 && (
                        availableCoupons.map((coupon) => {
                          const code = String(coupon?.couponCode || '').toUpperCase();
                          const isApplied = (couponCode || '').toUpperCase() === code && pricing.discountAmount > 0;
                          return (
                            <div
                              key={coupon?.id || code}
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: isApplied ? '1.5px solid #16A34A' : '1.5px dashed #CBD5E1',
                                borderRadius: '10px',
                                padding: '10px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '10px',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                  <span style={{
                                    fontSize: '13px',
                                    fontWeight: 900,
                                    color: '#16192E',
                                    letterSpacing: '0.04em',
                                    backgroundColor: '#F1F5F9',
                                    padding: '2px 7px',
                                    borderRadius: '5px'
                                  }}>
                                    {code}
                                  </span>
                                  {isApplied && (
                                    <span style={{ fontSize: '11px', color: '#16A34A', fontWeight: 800 }}>
                                      ✓ Applied
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '11.5px', color: '#64748B', lineHeight: 1.3 }}>
                                  {formatCouponDesc(coupon)}
                                </div>
                                {formatCouponExp(coupon) && (
                                  <div style={{ fontSize: '10.5px', color: '#94A3B8', marginTop: '2px' }}>
                                    {formatCouponExp(coupon)}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  if (isApplied) {
                                    if (handleRemoveCoupon) {
                                      handleRemoveCoupon();
                                    } else if (handleApplyCoupon) {
                                      handleApplyCoupon('');
                                    }
                                    if (setCouponCode) setCouponCode('');
                                  } else {
                                    if (setCouponCode) setCouponCode(code);
                                    if (handleApplyCoupon) handleApplyCoupon(code);
                                  }
                                }}
                                disabled={couponsLoading}
                                style={{
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '7px 15px',
                                  backgroundColor: isApplied ? '#FEE2E2' : '#16192E',
                                  color: isApplied ? '#DC2626' : '#FFFFFF',
                                  fontSize: '12px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                {isApplied ? 'Remove' : 'Apply'}
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  {pricing.discountAmount > 0 && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#16A34A', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>✓</span>
                      <span>Coupon applied! You saved {formatPrice(pricing.discountAmount)}</span>
                    </div>
                  )}

                  {couponsError && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#DC2626', fontWeight: 600 }}>
                      {couponsError}
                    </div>
                  )}
                </div>

                {/* Bill Summary */}
                <div
                  style={{
                    borderTop: '1px solid #F1F5F9',
                    paddingTop: '18px',
                    marginBottom: '20px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px', color: '#64748B' }}>
                    <span>Item Total</span>
                    <span style={{ fontWeight: 700, color: '#16192E' }}>{formatPrice(pricing.subtotal)}</span>
                  </div>

                  {deliveryOption === 'DELIVERY' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px', color: '#64748B' }}>
                      <span>Delivery Charges</span>
                      <span style={{ fontWeight: 700, color: '#16192E' }}>{formatPrice(pricing.deliveryCharge)}</span>
                    </div>
                  )}

                  {deliveryOption === 'DELIVERY' && deliverySlabMessage && (
                    <div style={{
                      margin: '-2px 0 12px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      backgroundColor: '#F0F9FF',
                      border: '1px solid #BAE6FD',
                      color: '#0369A1',
                      fontSize: '12px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>⚡</span>
                      <span>{deliverySlabMessage}</span>
                    </div>
                  )}

                  {pricing.discountAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px', color: '#16A34A' }}>
                      <span>Coupon Discount</span>
                      <span style={{ fontWeight: 700 }}>−{formatPrice(pricing.discountAmount)}</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '14px', color: '#64748B' }}>
                    <span>Taxes & Charges</span>
                    <span style={{ fontWeight: 700, color: '#16192E' }}>{formatPrice(pricing.taxCharges)}</span>
                  </div>

                  {/* Total Amount in Highlighted Peach/Amber Box (Exact to Picture) */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '14px 16px',
                      backgroundColor: '#FFF8F0',
                      border: '1px solid #FDE68A',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: 800,
                      color: '#16192E'
                    }}
                  >
                    <span>Total Amount</span>
                    <span style={{ fontSize: '18px', fontWeight: 900 }}>{formatPrice(pricing.grandTotal)}</span>
                  </div>
                </div>

                {/* Minimum Order Amount Deficit Warning */}
                {deliveryOption === 'DELIVERY' && pricing.belowMinimum && (
                  <div
                    style={{
                      marginBottom: '16px',
                      padding: '12px 14px',
                      borderRadius: '12px',
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#991B1B',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>⚠️</span>
                    <span>
                      Minimum order for delivery is {formatPrice(pricing.minOrderAmount)}. Add {formatPrice(pricing.deficit)} more to place order.
                    </span>
                  </div>
                )}

                {/* Store Closed Warning on Checkout */}
                {!restaurantOpen && (
                  <div
                    style={{
                      marginBottom: '16px',
                      padding: '12px 14px',
                      borderRadius: '12px',
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#991B1B',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>⚠️</span>
                    <span>The store is currently closed. Ordering is temporarily paused.</span>
                  </div>
                )}

                {/* Primary Checkout Button */}
                <button
                  type="button"
                  disabled={!restaurantOpen || (deliveryOption === 'DELIVERY' && pricing.belowMinimum)}
                  onClick={() => {
                    if (openCheckoutModal) {
                      openCheckoutModal();
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '9999px',
                    border: 'none',
                    backgroundColor: (!restaurantOpen || (deliveryOption === 'DELIVERY' && pricing.belowMinimum)) ? '#94A3B8' : '#16192E',
                    color: '#FFFFFF',
                    fontSize: '15px',
                    fontWeight: 800,
                    cursor: (!restaurantOpen || (deliveryOption === 'DELIVERY' && pricing.belowMinimum)) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: (!restaurantOpen || (deliveryOption === 'DELIVERY' && pricing.belowMinimum)) ? 'none' : '0 8px 24px rgba(22, 25, 46, 0.28)'
                  }}
                >
                  <span>
                    {!restaurantOpen
                      ? 'Store Currently Closed'
                      : (deliveryOption === 'DELIVERY' && pricing.belowMinimum)
                      ? `Add ${formatPrice(pricing.deficit)} More For Delivery`
                      : 'Proceed to Checkout'}
                  </span>
                  <span style={{ fontSize: '18px' }}>→</span>
                </button>

                {/* Powered by Trivana & Delivery Logistics Partner Marquee on Screen 4 */}
                <div style={{ marginTop: '24px' }}>
                  {renderTrivanaFooter()}
                </div>
              </>
            )}
          </div>

          {/* Global Drawer */}
          {renderDrawer()}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          FULL-SIZE PRODUCT DETAIL MODAL (Page-Level Luxury Sheet)
         ───────────────────────────────────────────────────────────────────────────── */}
      {selectedDetailProduct && (() => {
        const p = selectedDetailProduct;
        const pPricing = getProductPricingInfo(p, getProductDisplayPriceMeta);
        const pTitle = p.productName || p.name || 'Product';
        const pDesc = p.description || p.desc || '';
        const isVeg = p.isVeg !== false && p.itemType !== 'NON_VEG';

        const addonsTotal = (detailSelectedAddons || []).reduce((sum, item) => sum + Number(item.addonPrice || 0), 0);
        const unitTotal = pPricing.sellingPrice + addonsTotal;
        const finalTotal = unitTotal * (detailQuantity > 0 ? detailQuantity : 1);

        const groupedAddons = (detailAvailableAddons || []).reduce((acc, addon) => {
          const fallbackId = addon?.groupId || `group-${addon?.groupName || 'addons'}`;
          const groupId = String(fallbackId);
          if (!acc[groupId]) {
            acc[groupId] = {
              groupId,
              groupName: addon?.groupName || detailRulesByGroup?.[groupId]?.groupName || 'Add-ons',
              groupDisplayOrder: Number(addon?.groupDisplayOrder ?? detailRulesByGroup?.[groupId]?.displayOrder ?? 9999),
              options: []
            };
          }
          acc[groupId].options.push(addon);
          return acc;
        }, {});

        const sortedGroups = Object.values(groupedAddons).sort((a, b) => a.groupDisplayOrder - b.groupDisplayOrder);

        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.72)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              zIndex: 10000,
              display: 'flex',
              alignItems: isMobile ? 'flex-end' : 'center',
              justifyContent: 'center',
              padding: isMobile ? 0 : '16px'
            }}
            onClick={() => setSelectedDetailProduct(null)}
          >
            <div
              className="ndt-sheet-content"
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: isMobile ? '28px 28px 0 0' : '24px',
                width: '100%',
                maxWidth: isMobile ? '560px' : '780px',
                maxHeight: isMobile ? '92vh' : '88vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35)',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top Notch for mobile */}
              {isMobile && (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', paddingTop: '10px', backgroundColor: '#FFFFFF' }}>
                  <div style={{ width: '42px', height: '5px', backgroundColor: '#CBD5E1', borderRadius: '999px' }} />
                </div>
              )}

              {/* Scrollable Body */}
              <div style={{ overflowY: 'auto', flex: 1, position: 'relative' }}>
                {/* Top Large Hero Image */}
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: isMobile ? '230px' : '280px',
                    backgroundColor: '#F8FAFC',
                    overflow: 'hidden'
                  }}
                >
                  {p.imageUrl ? (
                    <img
                      src={resolveImageUrl(p.imageUrl)}
                      alt={pTitle}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '64px', color: '#94A3B8' }}>
                      🛍️
                    </div>
                  )}

                  {/* Close Button */}
                  <button
                    type="button"
                    onClick={() => setSelectedDetailProduct(null)}
                    style={{
                      position: 'absolute',
                      top: '14px',
                      right: '14px',
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(15, 23, 42, 0.65)',
                      backdropFilter: 'blur(6px)',
                      color: '#FFFFFF',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 700,
                      zIndex: 10,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    }}
                    aria-label="Close"
                  >
                    ✕
                  </button>

                  {/* Discount Tag on Image */}
                  {pPricing.hasDiscount && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '14px',
                        left: '14px',
                        background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                        color: '#FFFFFF',
                        padding: '5px 12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                        boxShadow: '0 4px 14px rgba(220, 38, 38, 0.45)',
                        textTransform: 'uppercase'
                      }}
                    >
                      {pPricing.discountPercent}% OFF
                    </div>
                  )}
                </div>

                {/* Info Section */}
                <div style={{ padding: '20px 22px 28px' }}>
                  {/* Category & Veg Badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        border: `1.5px solid ${isVeg ? '#16A34A' : '#DC2626'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        backgroundColor: '#FFFFFF',
                        flexShrink: 0
                      }}
                    >
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isVeg ? '#16A34A' : '#DC2626' }} />
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: isVeg ? '#16A34A' : '#DC2626' }}>
                      {isVeg ? 'Pure Veg' : 'Non-Veg'}
                    </span>
                    {p.productCategory?.categoryName && (
                      <span style={{ fontSize: '12px', color: '#64748B', fontWeight: 600 }}>
                        • {p.productCategory.categoryName}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h2
                    style={{
                      margin: '0 0 10px',
                      fontSize: isMobile ? '20px' : '23px',
                      fontWeight: 800,
                      color: '#16192E',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.3
                    }}
                  >
                    {pTitle}
                  </h2>

                  {/* Pricing Row: Selling Price + Strikethrough MRP + % OFF + Savings */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '22px', fontWeight: 900, color: '#16192E', letterSpacing: '-0.02em' }}>
                      {formatPrice(pPricing.sellingPrice)}
                    </span>
                    {pPricing.hasDiscount && (
                      <>
                        <span style={{ fontSize: '15px', color: '#94A3B8', textDecoration: 'line-through', fontWeight: 600 }}>
                          {formatPrice(pPricing.mrp)}
                        </span>
                        <span
                          style={{
                            background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                            color: '#FFFFFF',
                            fontSize: '11.5px',
                            fontWeight: 800,
                            padding: '3px 8px',
                            borderRadius: '6px',
                            letterSpacing: '0.02em',
                            boxShadow: '0 2px 6px rgba(220, 38, 38, 0.25)',
                            textTransform: 'uppercase'
                          }}
                        >
                          {pPricing.discountPercent}% OFF
                        </span>
                        {pPricing.discountAmount > 0 && (
                          <span
                            style={{
                              background: 'rgba(22, 163, 74, 0.1)',
                              color: '#16A34A',
                              fontSize: '12px',
                              fontWeight: 700,
                              padding: '3px 8px',
                              borderRadius: '6px',
                              border: '1px solid rgba(22, 163, 74, 0.2)'
                            }}
                          >
                            Save {formatPrice(pPricing.discountAmount)}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Full Description */}
                  {pDesc ? (
                    <div
                      style={{
                        fontSize: '14px',
                        color: '#475569',
                        lineHeight: '1.65',
                        marginBottom: '20px',
                        fontWeight: 500,
                        backgroundColor: '#F8FAFC',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        border: '1px solid #F1F5F9'
                      }}
                    >
                      {pDesc}
                    </div>
                  ) : null}

                  {/* Inline Customizations & Add-ons (Only rendered when item has customizations) */}
                  {sortedGroups.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {sortedGroups.map(({ groupId, groupName, options }) => {
                          const rule = detailRulesByGroup?.[groupId] || {};
                          const min = Number(rule?.minSelection || 0);
                          const max = Number(rule?.maxSelection || 99);
                          const isRequired = min > 0 || !!rule?.isRequired;
                          const isSingle = max === 1;

                          return (
                            <div
                              key={groupId}
                              style={{
                                border: '1px solid #E2E8F0',
                                borderRadius: '14px',
                                padding: '14px',
                                backgroundColor: '#FFFFFF',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <div>
                                  <span style={{ fontSize: '14px', fontWeight: 800, color: '#16192E' }}>
                                    {groupName}
                                  </span>
                                  <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px', fontWeight: 600 }}>
                                    {isSingle ? 'Choose 1 option' : (max < 99 ? `Select up to ${max}` : 'Select options')}
                                  </div>
                                </div>
                                {isRequired ? (
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: 800,
                                      color: '#DC2626',
                                      backgroundColor: '#FEE2E2',
                                      padding: '2px 8px',
                                      borderRadius: '999px',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em'
                                    }}
                                  >
                                    Required
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: 700,
                                      color: '#64748B',
                                      backgroundColor: '#F1F5F9',
                                      padding: '2px 8px',
                                      borderRadius: '999px'
                                    }}
                                  >
                                    Optional
                                  </span>
                                )}
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {options.map((addon) => {
                                  const optionKey = String(addon?.optionId || addon?.legacyAddonId || addon?.addonId || addon?.addonName || '');
                                  const isSelected = (detailSelectedAddons || []).some(
                                    (a) => String(a?.optionId || a?.legacyAddonId || a?.addonId || a?.addonName || '') === optionKey
                                  );
                                  const addPrice = Number(addon?.addonPrice || 0);

                                  return (
                                    <div
                                      key={optionKey}
                                      onClick={() => toggleDetailAddon(addon)}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px 12px',
                                        borderRadius: '10px',
                                        backgroundColor: isSelected ? '#F8FAFC' : '#FFFFFF',
                                        border: `1.5px solid ${isSelected ? '#16192E' : '#F1F5F9'}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div
                                          style={{
                                            width: '18px',
                                            height: '18px',
                                            borderRadius: isSingle ? '50%' : '5px',
                                            border: `2px solid ${isSelected ? '#16192E' : '#CBD5E1'}`,
                                            backgroundColor: isSelected ? '#16192E' : '#FFFFFF',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                          }}
                                        >
                                          {isSelected && (
                                            isSingle ? (
                                              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#FFFFFF' }} />
                                            ) : (
                                              <svg width="10" height="8" viewBox="0 0 12 10" fill="none">
                                                <path d="M1 5L4.5 8.5L11 1.5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              </svg>
                                            )
                                          )}
                                        </div>

                                        <span style={{ fontSize: '13.5px', fontWeight: isSelected ? 700 : 600, color: '#16192E' }}>
                                          {addon.addonName}
                                        </span>
                                      </div>

                                      <span style={{ fontSize: '13px', fontWeight: 700, color: isSelected ? '#16192E' : '#64748B' }}>
                                        {addPrice > 0 ? `+ ${formatPrice(addPrice)}` : 'Free'}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Special Instructions Note */}
                  <div style={{ marginTop: '18px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#16192E', marginBottom: '6px' }}>
                      Special instructions (optional)
                    </label>
                    <input
                      type="text"
                      maxLength={100}
                      value={detailSpecialInstructions}
                      onChange={(e) => setDetailSpecialInstructions(e.target.value)}
                      placeholder="e.g. Less spicy, keep sauce separate..."
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        border: '1px solid #CBD5E1',
                        fontSize: '13.5px',
                        color: '#16192E',
                        backgroundColor: '#FFFFFF',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Sticky Bottom Action Bar */}
              <div
                style={{
                  borderTop: '1px solid #F1F5F9',
                  padding: '14px 20px',
                  backgroundColor: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.04)'
                }}
              >
                {/* Stepper */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#F1F5F9',
                    borderRadius: '9999px',
                    padding: '6px 12px',
                    gap: '12px'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setDetailQuantity((q) => Math.max(1, q - 1))}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#16192E',
                      fontSize: '18px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      padding: '0 4px',
                      lineHeight: 1
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontSize: '14px', fontWeight: 800, color: '#16192E', minWidth: '16px', textAlign: 'center' }}>
                    {detailQuantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setDetailQuantity((q) => q + 1)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#16192E',
                      fontSize: '18px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      padding: '0 4px',
                      lineHeight: 1
                    }}
                  >
                    +
                  </button>
                </div>

                {/* Add to Cart CTA */}
                {(() => {
                  const outOfStock = isItemOutOfStock(p);
                  return (
                    <button
                      type="button"
                      disabled={outOfStock}
                      onClick={handleConfirmDetailAddToCart}
                      style={{
                        flex: 1,
                        padding: '14px 20px',
                        backgroundColor: outOfStock ? '#94A3B8' : '#16192E',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '9999px',
                        fontSize: '14.5px',
                        fontWeight: 800,
                        cursor: outOfStock ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: outOfStock ? 'none' : '0 8px 20px rgba(22, 25, 46, 0.28)',
                        transition: 'transform 0.15s ease'
                      }}
                    >
                      <span>{outOfStock ? 'Out of Stock' : 'Add to Cart'}</span>
                      <span>{outOfStock ? '' : `${formatPrice(finalTotal)} →`}</span>
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ─────────────────────────────────────────────────────────────────────────────
          THEME-MATCHED LUXURY CUSTOMIZATION MODAL (Direct Card + Add)
         ───────────────────────────────────────────────────────────────────────────── */}
      {showAddonModal && currentProduct && (() => {
        const p = currentProduct;
        const pPricing = getProductPricingInfo(p, getProductDisplayPriceMeta);
        const pTitle = p.productName || p.name || 'Product';
        const isVeg = p.isVeg !== false && p.itemType !== 'NON_VEG';

        const addonsTotal = (selectedAddons || []).reduce((sum, item) => sum + Number(item.addonPrice || 0), 0);
        const currentTotal = pPricing.sellingPrice + addonsTotal;

        const grouped = (availableAddons || []).reduce((acc, addon) => {
          const fallbackId = addon?.groupId || `group-${addon?.groupName || 'addons'}`;
          const groupId = String(fallbackId);
          if (!acc[groupId]) {
            acc[groupId] = {
              groupId,
              groupName: addon?.groupName || selectionRulesByGroup?.[groupId]?.groupName || 'Add-ons',
              groupDisplayOrder: Number(addon?.groupDisplayOrder ?? selectionRulesByGroup?.[groupId]?.displayOrder ?? 9999),
              options: []
            };
          }
          acc[groupId].options.push(addon);
          return acc;
        }, {});

        const sortedGroups = Object.values(grouped).sort((a, b) => a.groupDisplayOrder - b.groupDisplayOrder);

        const handleApplyAddons = () => {
          if (typeof validateGroupSelections === 'function') {
            if (!validateGroupSelections()) return;
          }
          if (typeof addToCartWithAddons === 'function') {
            addToCartWithAddons(currentProduct, selectedAddons);
          } else if (typeof closeAddonModal === 'function') {
            closeAddonModal();
          }
        };

        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.72)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              zIndex: 10001,
              display: 'flex',
              alignItems: isMobile ? 'flex-end' : 'center',
              justifyContent: 'center',
              padding: isMobile ? 0 : '16px'
            }}
            onClick={() => closeAddonModal && closeAddonModal()}
          >
            <div
              className="ndt-sheet-content"
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: isMobile ? '28px 28px 0 0' : '24px',
                width: '100%',
                maxWidth: '540px',
                maxHeight: isMobile ? '90vh' : '85vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35)',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Grab notch */}
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
                <div style={{ width: '42px', height: '5px', backgroundColor: '#CBD5E1', borderRadius: '999px' }} />
              </div>

              {/* Modal Header */}
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: '18px',
                      height: '18px',
                      border: `1.5px solid ${isVeg ? '#16A34A' : '#DC2626'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      flexShrink: 0
                    }}
                  >
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isVeg ? '#16A34A' : '#DC2626' }} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#16192E', letterSpacing: '-0.015em' }}>
                      {pTitle}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#64748B' }}>Base Price:</span>
                      <span style={{ fontSize: '13.5px', fontWeight: 800, color: '#16192E' }}>
                        {formatPrice(pPricing.sellingPrice)}
                      </span>
                      {pPricing.hasDiscount && (
                        <>
                          <span style={{ fontSize: '12px', color: '#94A3B8', textDecoration: 'line-through', fontWeight: 600 }}>
                            {formatPrice(pPricing.mrp)}
                          </span>
                          <span
                            style={{
                              fontSize: '10.5px',
                              fontWeight: 800,
                              color: '#16A34A',
                              backgroundColor: '#DCFCE7',
                              padding: '1px 5px',
                              borderRadius: '4px'
                            }}
                          >
                            {pPricing.discountPercent}% OFF
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => closeAddonModal && closeAddonModal()}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#F1F5F9',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#64748B'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Customizations Body */}
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                {loadingAddons ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748B', fontSize: '13px', fontWeight: 600 }}>
                    Loading customizations...
                  </div>
                ) : sortedGroups.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#64748B', fontSize: '13px' }}>
                    No custom options available for this item.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {sortedGroups.map(({ groupId, groupName, options }) => {
                      const rule = selectionRulesByGroup?.[groupId] || {};
                      const min = Number(rule?.minSelection || 0);
                      const max = Number(rule?.maxSelection || 99);
                      const isRequired = min > 0 || !!rule?.isRequired;
                      const isSingle = max === 1;

                      return (
                        <div
                          key={groupId}
                          style={{
                            border: '1px solid #E2E8F0',
                            borderRadius: '14px',
                            padding: '14px',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <div>
                              <span style={{ fontSize: '14px', fontWeight: 800, color: '#16192E' }}>
                                {groupName}
                              </span>
                              <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px', fontWeight: 600 }}>
                                {isSingle ? 'Choose 1 option' : (max < 99 ? `Select up to ${max}` : 'Select options')}
                              </div>
                            </div>
                            {isRequired ? (
                              <span style={{ fontSize: '10px', fontWeight: 800, color: '#DC2626', backgroundColor: '#FEE2E2', padding: '2px 8px', borderRadius: '999px', textTransform: 'uppercase' }}>
                                Required
                              </span>
                            ) : (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', backgroundColor: '#F1F5F9', padding: '2px 8px', borderRadius: '999px' }}>
                                Optional
                              </span>
                            )}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {options.map((addon) => {
                              const optionKey = String(addon?.optionId || addon?.legacyAddonId || addon?.addonId || addon?.addonName || '');
                              const isSelected = (selectedAddons || []).some(
                                (a) => String(a?.optionId || a?.legacyAddonId || a?.addonId || a?.addonName || '') === optionKey
                              );
                              const addPrice = Number(addon?.addonPrice || 0);

                              return (
                                <div
                                  key={optionKey}
                                  onClick={() => toggleAddonSelection && toggleAddonSelection(addon)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px 12px',
                                    borderRadius: '10px',
                                    backgroundColor: isSelected ? '#F8FAFC' : '#FFFFFF',
                                    border: `1.5px solid ${isSelected ? '#16192E' : '#F1F5F9'}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div
                                      style={{
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: isSingle ? '50%' : '5px',
                                        border: `2px solid ${isSelected ? '#16192E' : '#CBD5E1'}`,
                                        backgroundColor: isSelected ? '#16192E' : '#FFFFFF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                      }}
                                    >
                                      {isSelected && (
                                        isSingle ? (
                                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#FFFFFF' }} />
                                        ) : (
                                          <svg width="10" height="8" viewBox="0 0 12 10" fill="none">
                                            <path d="M1 5L4.5 8.5L11 1.5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        )
                                      )}
                                    </div>
                                    <span style={{ fontSize: '13.5px', fontWeight: isSelected ? 700 : 600, color: '#16192E' }}>
                                      {addon.addonName}
                                    </span>
                                  </div>

                                  <span style={{ fontSize: '13px', fontWeight: 700, color: isSelected ? '#16192E' : '#64748B' }}>
                                    {addPrice > 0 ? `+ ${formatPrice(addPrice)}` : 'Free'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Special Instructions */}
                {setPendingSpecialInstructions && (
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#16192E', marginBottom: '6px' }}>
                      Special instructions (optional)
                    </label>
                    <input
                      type="text"
                      maxLength={100}
                      value={pendingSpecialInstructions || ''}
                      onChange={(e) => setPendingSpecialInstructions(e.target.value)}
                      placeholder="e.g. Less spicy, keep sauce separate..."
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: '1px solid #CBD5E1',
                        fontSize: '13px',
                        color: '#16192E',
                        backgroundColor: '#FFFFFF',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Modal Bottom CTA */}
              <div
                style={{
                  borderTop: '1px solid #F1F5F9',
                  padding: '14px 20px',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <button
                  type="button"
                  onClick={handleApplyAddons}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    backgroundColor: '#16192E',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '9999px',
                    fontSize: '14.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 8px 20px rgba(22, 25, 46, 0.28)'
                  }}
                >
                  <span>{editingCartItemId ? 'Update Item' : 'Add Item'}</span>
                  <span>{formatPrice(currentTotal)} →</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default NewDevelopmentTheme;
